import { TUNING } from '~/config/tuning'
import { ascensionPressureMult } from '~/data/ascensions'
import { getCreatureDef } from '~/data/creatures'
import { FIRST_DISPATCH } from '~/data/firstDispatch'
import { emptyStats } from '~/utils/battle'
import { funnelCampaign, funnelPractice, track } from '~/utils/funnel'
import { makeCreature } from '~/utils/roster'
import { ELEMENT_INFO } from '~/data/elements'
import { enemyArt } from '~/data/enemyArt'
import { CHAMPION_SLUGS } from '~/data/missions'
import { bossRoomId, forestById, isBossRoomId, isLandscapeViewport, pairedForestId, poiTravelLength, puzzleHallId, rollForestId, rollWideForestId } from '~/data/forests'
import { BANNER_TAX, ENRAGE_CAP, ENRAGE_SECONDS, VORACIOUS_REQ_BONUS } from '~/data/eliteModifiers'
import { FOG_REVEAL_SECONDS, hasWeeklyMod, suddenDeathClamp, weeklySpawnMult } from '~/data/weeklyMods'
import { hasRelic } from '~/data/relics'
import { rollTrainingOptions } from '~/data/rewards'
import { ELEMENTS, type FieldDecoration, type Mission, type RunState } from '~/types/game'
import { bankTideStack, canLearnSkill, engineCapLifted, enrageSeconds, escalationMult, expiryDamageFor, hibernalBank, MOMENTUM_STACK_CAP,predictSplitRequirements, WINDFALL_GOLD, steadyGroundExpiryMult } from '~/utils/battle'
import { dangerForDistance, puzzleTeamMatchesAnswer, GOLEM_CRYSTAL_CAP, GOLEM_CRYSTAL_RESPAWN_SECONDS, GOLEM_CRYSTAL_RESPAWN_SLOW_SECONDS, GOLEM_EXPIRE_SECONDS, isCombatNode, SCIENTIST_EXPIRE_SECONDS, SCIENTIST_MEDDLE_RESPAWN_SECONDS, type CombatNodeType, type MissionGenOptions } from '~/utils/missiongen'
import { ensureStarweaveNode, hasStarweave, starweaveAfterDamage, starweaveArmRegroup, starweaveOnFailedReturn, starweaveWindfallGold } from '~/utils/starweaveRun'
import { canStarweaveDoubleTraining } from '~/utils/starweaveRewards'
import { enrageArmsAt, onFirstRunRails, tallyFirstRunDamage } from '~/utils/firstRunRails'

/** Field-map board dimensions (board units: x 0–100, y in pixels). */
export const FIELD_HEIGHT = 460
export const FIELD_HOME = { x: 50, y: 418 }
/** Rough pixels per x-unit, to make distances isotropic-ish. */
const X_SCALE = 4.2
/** Pixel distance → travel distance units. */
export const DISTANCE_SCALE = 19

/**
 * The live encounter engine (gamedesign.md §11–§18 + travel system).
 * Missions appear at field-map sites; dispatched teams walk out at their
 * slowest member's pace, fight, and walk home before freeing up.
 * Dispatch commits: the expire timer stops the moment Fight is clicked.
 */
export function useEncounter() {
  const { run, getNode, completeActiveNode } = useRun()
  const sfx = useSfx()

  const encounter = computed(() => run.value?.encounter ?? null)
  const activeNode = computed(() => getNode(run.value?.activeNodeId ?? null))

  const selectedMissionId = useState<string | null>('encounter-selected-mission', () => null)
  /** 🎓 Countdown until this node's training-mission roll (v0.79). */
  const trainingDelay = useState('encounter-training-delay', () => 0)
  const selectedMission = computed(
    () =>
      encounter.value?.missions.find(
        m => m.id === selectedMissionId.value && m.status === 'waiting',
      ) ?? null,
  )
  /** First mission whose fight timer finished; drives the result overlay. */
  const resolvingMission = computed(
    () => encounter.value?.missions.find(m => m.status === 'resolving') ?? null,
  )

  function nodeConfig() {
    const node = activeNode.value
    if (!node || !isCombatNode(node)) return null
    return TUNING.encounters.byNodeType[node.type as CombatNodeType]
  }

  /** The active field's trailhead (board units) — the forest's painted
   * camp when one is up, the classic home otherwise. 🏟️ v6.28: in a
   * boss arena the team has already walked in and waits ON the ring, so
   * it sets out from the rim (per Javi: "make Vessk's walk not so long"
   * — the old trek from the bottom stairs was 12 units, ~19s). */
  function fieldHome(): { x: number; y: number } {
    const forest = forestById(run.value?.encounter?.forestId)
    const origin = forest?.arena?.muster ?? forest?.home
    return origin
      ? { x: origin.x, y: (origin.y / 100) * FIELD_HEIGHT }
      : FIELD_HOME
  }

  /** Pixel-space distance from home for a board position. */
  function pixelDistance(x: number, y: number) {
    const home = fieldHome()
    return Math.hypot((x - home.x) * X_SCALE, y - home.y)
  }

  /** Travel distance units for a board position (v1.21, per Javi): on a
   * painted forest the team walks the trail, so distance is the PATH
   * length — a winding trail honestly takes longer than a site that sits
   * close as the crow flies. Classic board: straight line, as ever. */
  function travelDistance(x: number, y: number, poi?: number): number {
    const forest = forestById(run.value?.encounter?.forestId)
    const site = poi !== undefined ? forest?.pois[poi] : undefined
    const px = site
      ? poiTravelLength(site) * (FIELD_HEIGHT / 100) // % units → board px
      : pixelDistance(x, y)
    return Math.min(
      TUNING.travel.maxDistance,
      Math.max(TUNING.travel.minDistance, Math.round(px / DISTANCE_SCALE)),
    )
  }

  /** 🦈 v5.42 Voracious: which fallen missions may rise. Boss kinds run
   * their own reform machinery; training grounds and a mission that
   * already rose stay down. */
  function voraciousEligible(mission: Mission): boolean {
    return !mission.voraciousReborn && !mission.training
      && mission.tier !== 'boss' && !isBossSplit(mission)
      && mission.bossSkill !== 'scientist'
  }

  /** 👿 v5.66 Twin Curses: an elite can carry TWO modifiers — every
   * rule that used to read `.modifier` asks through here instead. */
  function nodeHasMod(id: string): boolean {
    const n = activeNode.value
    return !!n && (n.modifier === id || n.modifier2 === id)
  }

  /** 🦈 The risen copy: fresh timers, +VORACIOUS_REQ_BONUS spread over
   * its posted elements, flagged so it stays down the second time. */
  function voraciousRise(mission: Mission): Mission {
    const required = { ...mission.required }
    const posted = ELEMENTS.filter(e => required[e] > 0)
    const pool = posted.length > 0 ? posted : [...ELEMENTS]
    for (let i = 0; i < VORACIOUS_REQ_BONUS; i++) {
      required[pool[Math.floor(Math.random() * pool.length)]!] += 1
    }
    return {
      ...mission,
      id: makeId('m'),
      required,
      status: 'waiting',
      assignedCreatureIds: [],
      assignedChance: null,
      firstDispatch: undefined, // 🦈 v7.18: a new mission, not the one that opened the node
      expireTimer: mission.expireTotal,
      fightTimer: mission.fightTotal,
      travelRemaining: 0,
      removeIn: null,
      expiryDamage: 0,
      enrageBonus: 0,
      enrageTick: 0,
      voraciousReborn: true,
    }
  }

  /** Neighborhood scenery: cottages, trees, and stones in the woods. */
  function generateDecorations(): FieldDecoration[] {
    const icons = ['🌲', '🌲', '🌳', '🌳', '🏡', '🏡', '🪨', '🌼', '🍄', '🌾']
    const out: FieldDecoration[] = []
    for (const icon of icons) {
      for (let attempt = 0; attempt < 6; attempt++) {
        const x = randInt(4, 96)
        const y = randInt(40, 400)
        if (pixelDistance(x, y) < 70) continue
        if (out.some(d => Math.hypot((d.x - x) * X_SCALE, d.y - y) < 45)) continue
        out.push({ x, y, icon })
        break
      }
    }
    return out
  }

  /** Placement zones: a loose 3×3 grid keeps markers from overlapping. */
  const ZONE_X = [18, 50, 82]
  const ZONE_Y = [100, 210, 320]

  function zoneOf(x: number, y: number): number {
    const col = ZONE_X.reduce((best, cx, i) => (Math.abs(cx - x) < Math.abs(ZONE_X[best]! - x) ? i : best), 0)
    const row = ZONE_Y.reduce((best, cy, i) => (Math.abs(cy - y) < Math.abs(ZONE_Y[best]! - y) ? i : best), 0)
    return row * ZONE_X.length + col
  }

  /** 🌲 A forest POI's position in board units (x %, y px). */
  function poiBoardPosition(forestId: string, index: number): { x: number; y: number } {
    const poi = forestById(forestId)?.pois[index]
    if (!poi) return { x: 50, y: 200 }
    return { x: poi.x, y: (poi.y / 100) * FIELD_HEIGHT }
  }

  /**
   * Rolls an open spot for a new mission. Forest fields (v1.20): missions
   * land on the painting's points of interest — a random FREE one (or the
   * farthest, for preferFar). The classic board keeps its 3×3 zone grid.
   * Returns null when every site is taken (the spawn retries shortly).
   */
  function rollPosition(preferFar: boolean): { x: number; y: number; poi?: number } | null {
    const enc = run.value?.encounter
    const forest = forestById(enc?.forestId)
    // Boss throne rooms have no trail POIs — their spawns (Dr. Vex's
    // meddling) fall through to the classic zone grid below.
    if (enc && forest && forest.pois.length > 0) {
      const taken = new Set(
        enc.missions
          .filter(m => ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status))
          .map(m => m.poi)
          .filter((p): p is number => p !== undefined),
      )
      // 🏗️ v6.92 Robodude's Hold Position: the site he stands on is spoken for.
      for (const creature of run.value?.roster ?? []) if (creature.stationedPoi !== undefined) taken.add(creature.stationedPoi)
      const free = forest.pois.map((_, i) => i).filter(i => !taken.has(i))
      if (free.length === 0) return null
      // "Far" means a long walk up the trail, not crow-flies (v1.21).
      const distOf = (i: number) => poiTravelLength(forest.pois[i])
      const index = preferFar
        ? free.reduce((best, i) => (distOf(i) > distOf(best) ? i : best))
        : free[randInt(0, free.length - 1)]!
      return { ...poiBoardPosition(forest.id, index), poi: index }
    }
    // 👑 Boss throne rooms (v1.96, per Javi): the extra fights — Dr.
    // Vex's meddling, Myriad's shards, the Yin/Yang halves — spawn on a
    // RING around the boss platform instead of scattering across the
    // room. 🏟️ v6.28 (per Javi: "no conflicts where bosses spawn"): the
    // team's half of the ring stays clear — the bottom rim on the
    // portrait rooms, the whole LEFT half on the wide ones (the team
    // lines the left rim; only the right half, where the boss walks in
    // over the bridge, takes spawns). The portrait ring is a little
    // wider and taller than the v1.96 one so the side spots' labels
    // hang clear of the team's outermost creatures on the inner rim.
    const room = forestById(enc?.forestId)
    if (enc && room && isBossRoomId(room.id) && room.bossSpot) {
      const cx = room.bossSpot.x
      const cy = (room.bossSpot.y / 100) * FIELD_HEIGHT
      const wide = room.arena?.teamSide === 'left'
      const RING_DEG = wide
        ? [-90, 90, -45, 45, -20, 20]
        : [-90, -140, -40, -175, -5, -115, -65]
      const [ringX, ringY] = wide ? [27, 0.15] : [32, 0.2]
      const spots = RING_DEG.map((deg) => {
        const rad = (deg * Math.PI) / 180
        return {
          x: Math.min(88, Math.max(12, cx + ringX * Math.cos(rad))),
          y: Math.min(FIELD_HEIGHT - 60, Math.max(40, cy + ringY * FIELD_HEIGHT * Math.sin(rad))),
        }
      })
      const live = (enc.missions ?? []).filter(m => ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status))
      const freeSpots = spots.filter(p => !live.some(m => Math.abs(m.x - p.x) < 9 && Math.abs(m.y - p.y) < 34))
      if (freeSpots.length === 0) return null
      const pick = preferFar
        ? freeSpots.reduce((best, p) => (p.y < best.y ? p : best))
        : freeSpots[randInt(0, freeSpots.length - 1)]!
      return { x: Math.round(pick.x), y: Math.round(pick.y) }
    }
    const occupied = new Set((enc?.missions ?? []).map(m => zoneOf(m.x, m.y)))
    const free: number[] = []
    for (let zone = 0; zone < ZONE_X.length * ZONE_Y.length; zone++) {
      if (!occupied.has(zone)) free.push(zone)
    }
    if (free.length === 0) return null
    const zone = preferFar
      ? free.reduce((best, z) => (Math.floor(z / 3) < Math.floor(best / 3) ? z : best))
      : free[randInt(0, free.length - 1)]!
    return {
      x: ZONE_X[zone % 3]! + randInt(-3, 3),
      y: ZONE_Y[Math.floor(zone / 3)]! + randInt(-15, 15),
    }
  }

  /** Basalt's crystal ring: five points of a pentagram around the boss
   * at mid-field (50, 200), spread wide (v0.62) so the labels breathe.
   * A smashed crystal reforms at its own point. */
  const PENTAGRAM_SPOTS = [
    { x: 50, y: 68 },
    // v6.28: the side points ride a touch higher and wider so their
    // labels clear the team standing on the inner ring below them.
    { x: 83, y: 150 },
    { x: 70, y: 310 },
    { x: 30, y: 310 },
    { x: 17, y: 150 },
  ]
  /** 🏟️ v6.28: the WIDE arena's ring — the RIGHT half of the painted
   * second ring, so the team's left rim stays clear (the boss's bridge
   * walk crosses the middle point for a moment, nothing more). */
  const PENTAGRAM_SPOTS_WIDE = [
    { x: 50, y: 156 },
    { x: 65, y: 179 },
    { x: 73, y: 235 },
    { x: 65, y: 290 },
    { x: 50, y: 313 },
  ]

  function freePentagramSpot(liveMinions: Mission[]): { x: number; y: number } | null {
    const room = forestById(run.value?.encounter?.forestId)
    const spots = room?.arena?.teamSide === 'left' ? PENTAGRAM_SPOTS_WIDE : PENTAGRAM_SPOTS
    return spots.find(spot =>
      !liveMinions.some(m => Math.abs(m.x - spot.x) < 4 && Math.abs(m.y - spot.y) < 12),
    ) ?? null
  }

  /** onMissionSpawn hook: free placement; distance drives danger scaling.
   * Bosses take the center of the field (their entrance drops them there). */
  function spawnMission(
    node: NonNullable<typeof activeNode.value>, spawnIndex: number, lane = 'mission',
    /** 🎓 v7.38: a practice lesson's own asks (see MissionGenOptions.required). */
    required?: MissionGenOptions['required'],
    /** 📱 M3: a practice lesson's pinned foe (see MissionGenOptions.name). */
    name?: MissionGenOptions['name'],
  ): Mission | null {
    const state = run.value
    // The boss stands on the throne room's painted CENTER RING (v1.43,
    // per Javi; entering through the top gate was v1.42) — each arena's
    // ring height comes from its def. Classic board keeps center-field.
    const room = forestById(state?.encounter?.forestId)
    const bossSpot = room?.bossSpot
      ? { x: room.bossSpot.x, y: Math.round((room.bossSpot.y / 100) * FIELD_HEIGHT) }
      : { x: 50, y: 200 }
    const position: { x: number; y: number; poi?: number } | null
      = node.type === 'boss' ? bossSpot : rollPosition(false)
    if (!position) return null
    const { x, y } = position
    const distance = travelDistance(x, y, position.poi)
    // Ascension pressure (v0.80): A2/A8 harden every rolled mission.
    // 🦴 v5.57 (per Javi): in a daily the Nth mission of a node ROLLS
    // the same content for everyone (elements, spread, slots, timers) —
    // keyed node+index, so spawn TIMING and the position roll above
    // stay live without disturbing the stream. Absolute numbers still
    // scale with the rolled site's distance.
    const mission = withRunStream(state ?? {}, `${lane}/${node.id}/${spawnIndex}`, () =>
      generateMission(node, state?.act ?? 1, dangerForDistance(distance), {
        pressureMult: ascensionPressureMult(state?.ascension ?? 0),
        // 🎯 v7.29: the opening wave of a two-threat node covers both.
        spawnIndex,
        // 🚩 v6.00 (per Javi): on banner ground the champion is the
        // SECOND arrival, always.
        forceChampion: lane === 'mission' && spawnIndex === 1 && nodeHasMod('banner'),
        // 🗓️⚗️ v5.66: the weekly law reshapes generation (rush/fog/
        // flood/freshlegs/headstart/prisma).
        weeklyMod: state?.weeklyMod ?? null,
        required,
        name,
        // 🎓 v7.41 (per Javi, D8): a first run's first two rows deal easy only.
        easyOnly: onFirstRunRails(state, node),
      }))
    // 🌫️ Fog of War week: the demands surface after a beat on the field.
    if (hasWeeklyMod(state, 'fog') && mission.tier !== 'boss' && !mission.training) {
      mission.fogRemaining = FOG_REVEAL_SECONDS
    }
    mission.x = x
    mission.y = y
    // ⏫ v4.72: the longer the enrage has run, the harder every NEW
    // arrival hits — baked here so the card shows the true bite.
    if (mission.damage > 0) {
      mission.damage = Math.round(mission.damage * escalationMult(enrageSeconds(state?.encounter)))
    }
    // v2.30 (per Javi): Yin & Yang's intertwined art rides taller than
    // the other bosses — stand it a step up the ring so it doesn't
    // crowd the team column. (Mirrored in normalizeRun's ring snap.)
    if (node.type === 'boss' && mission.bossSkill === 'yinyang') mission.y -= 22
    mission.poi = position.poi
    mission.distance = distance
    mission.expireTimer = modifiedExpireTimer(state, mission.expireTimer, spawnIndex)
    mission.expireTotal = mission.expireTimer
    // 📔 v6.03 (3-state codex, supersedes v5.12): a spawn is a
    // SIGHTING for every painted foe, champions included — beating it
    // is what MASTERS the entry now.
    const enemySlug = mission.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    if (enemyArt(enemySlug)) useCollection().record('enemies', enemySlug, { announce: false })
    // 🚩 v5.43 Warlord's Banner (elite modifier): while the champion
    // stands, every OTHER mission spawns +2 harder. The tax is recorded
    // so felling the champion refunds it from waiting missions.
    if (nodeHasMod('banner') && !state?.encounter?.bannerLifted
      && !CHAMPION_SLUGS.has(enemySlug) && mission.tier !== 'boss') {
      const posted = ELEMENTS.filter(e => mission.required[e] > 0)
      const pool = posted.length > 0 ? posted : [...ELEMENTS]
      const taxed: typeof pool = []
      for (let i = 0; i < BANNER_TAX; i++) {
        const el = pool[Math.floor(Math.random() * pool.length)]!
        mission.required[el] += 1
        taxed.push(el)
      }
      mission.bannerTax = taxed
    }
    // Knockout boss skill — each attempt benches a random creature.
    if (mission.bossSkill === 'knockout' && state && state.roster.length > 0) {
      // 😴 v5.27 (per Javi): more sleepers on higher ascensions — but at
      // least one ally always stays awake to fight. v7.52: the count
      // follows the roster too (a team of three loses one).
      const wanted = Math.min(bossAfflictionCount(state.act, state.roster.length), Math.max(1, state.roster.length - 1))
      mission.knockedOutCreatureIds = pickRandomIds(state.roster.map(c => c.id), wanted)
      mission.knockedOutCreatureId = null
    }
    return mission
  }

  /** Builds the encounter when the player first arrives (and after reloads). */
  function ensureEncounter() {
    const state = run.value
    const node = activeNode.value
    if (!state || state.phase !== 'encounter' || state.encounter) return
    if (!node?.preview || !isCombatNode(node)) return
    ensureStarweaveNode(state)
    // Puzzle nodes (v0.59): all missions at once, mirrored from the
    // roster; nothing else ever spawns.
    const puzzleMissions = node.type === 'puzzle'
      ? generatePuzzleMissions(state.roster, state.act)
      : []
    // 🌲 Every combat field plays on a painted map from the ACT'S biome
    // (v1.39; random forest since v1.20): field nodes roll one of the
    // biome's trail maps, boss nodes take its throne room (v1.42) — the
    // boss enters through the top gate, the team musters at the bottom.
    // ⚔️ Gauntlet acts 4+ cycle the run's drawn biomes (v2.34).
    const themes = state.actThemes ?? []
    const theme = themes[(state.act - 1) % Math.max(1, themes.length)] ?? 'forest'
    // 🖥️ v5.35: a landscape viewport at node entry draws from the WIDE
    // set. 🏟️🧩 v6.28: boss arenas and puzzle halls come in both shapes
    // too (Javi's PC repaints) — the boss arena is picked by shape here
    // and never swaps; the puzzle hall pairs like the band maps.
    // 🦴 v5.57: in a daily the painting — and so the POI layout the
    // missions land on — is skeleton, keyed per node. Orientation stays
    // the player's own: portrait players share one pick, landscape
    // players share the wide pick from the same stream.
    const forestId = node.type === 'boss'
      ? bossRoomId(theme, isLandscapeViewport())
      : node.type === 'puzzle'
        ? puzzleHallId(theme, isLandscapeViewport())
        : withRunStream(state, `forest/${node.id}`, () =>
          (isLandscapeViewport() ? rollWideForestId(theme) : null) ?? rollForestId(theme))
    const forest = forestById(forestId)
    const PUZZLE_SPOTS = [
      { x: 22, y: 110 }, { x: 78, y: 110 }, { x: 22, y: 260 }, { x: 78, y: 260 },
    ]
    puzzleMissions.forEach((mission, index) => {
      let walkPx: number
      if (forest) {
        const pi = index % forest.pois.length
        const poi = forest.pois[pi]!
        mission.x = poi.x
        mission.y = (poi.y / 100) * FIELD_HEIGHT
        mission.poi = pi
        // Trail length, % → board px (v1.21): teams walk the painted path.
        walkPx = poiTravelLength(poi) * (FIELD_HEIGHT / 100)
      } else {
        const spot = PUZZLE_SPOTS[index] ?? { x: 50, y: 180 }
        mission.x = spot.x
        mission.y = spot.y
        walkPx = Math.hypot((mission.x - FIELD_HOME.x) * X_SCALE, mission.y - FIELD_HOME.y)
      }
      mission.distance = Math.min(
        TUNING.travel.maxDistance,
        Math.max(TUNING.travel.minDistance, Math.round(walkPx / DISTANCE_SCALE)),
      )
    })
    state.encounter = {
      nodeId: node.id,
      goal: node.type === 'puzzle' ? puzzleMissions.length : node.preview.requiredSuccesses,
      successes: 0,
      forestId,
      // The field starts empty — the first mission arrives a moment after
      // the player does, and the opening wave trickles in one at a time.
      // Bosses don't wait, and puzzles lay everything out immediately.
      missions: puzzleMissions,
      // Painted forests carry their own scenery — emoji props are for
      // the classic (boss) board only.
      decorations: forest ? [] : generateDecorations(),
      returnTrips: [],
      // null, not Infinity — same JSON round-trip footgun as the gold
      // clock below (v1.12: a reloaded puzzle node grew a normal mission).
      nextSpawnIn: node.type === 'puzzle'
        ? null
        : node.type === 'boss'
          ? 0
          : randInt(TUNING.encounters.firstSpawnDelay[0], TUNING.encounters.firstSpawnDelay[1]),
      status: 'active',
      safetyNetUsed: false,
      medicBandUsed: false,
      perfectPlanUsed: false,
      fastDeployUsed: false,
      teamJournalPrimed: false,
      focusBellUses: 0,
      // Gold missions run their own clock; bosses and puzzles keep a
      // clean field. null (NOT Infinity, which JSON turns into null
      // anyway and a migration then misread — v0.88) = never here.
      nextGoldSpawnIn: node.type === 'boss' || node.type === 'puzzle'
        ? null
        : randInt(TUNING.gold.spawnEvery[0], TUNING.gold.spawnEvery[1]),
      // Dr. Vex meddles moments after arrival; Basalt's crystal ring is
      // already up when the player walks in (v0.61).
      nextMinionSpawnIn: node.preview.boss?.skill === 'golem'
        ? 0
        : node.preview.boss?.skill === 'scientist' ? 2 : undefined,
      spawnCount: 0,
      goldSpawnCount: 0,
      everAssignedIds: [],
      succeededIds: [],
      damageTaken: 0,
      // The generating splits survive trial cleanup for the summary.
      puzzleAnswers: node.type === 'puzzle'
        ? puzzleMissions.map(m => ({ name: m.name, creatureIds: m.puzzleAnswer ?? [] }))
        : undefined,
    }
    // Collection: facing a boss logs it in the codex (fought, not beaten).
    if (node.type === 'boss' && node.preview.boss?.skill) {
      useCollection().record('bosses', node.preview.boss.skill)
    }
    // 🎓 v7.04 First Dispatch: no random spawns, no gold - the script
    // (data/firstDispatch.ts) deals each beat from tick().
    if (state.mode === 'tutorial') {
      state.encounter.nextSpawnIn = null
      state.encounter.nextGoldSpawnIn = null
      state.encounter.tutorialStep = 0
      state.encounter.tutorialClock = FIRST_DISPATCH.beatGap
    }
    // Fresh node: training tails the first mission by a fixed beat.
    trainingDelay.value = TUNING.training.spawnAfterFirst
  }

  /** Returns the wound that landed (🎓 v7.40: the practice's 1-health floor can shrink it). */
  function damagePlayer(amount: number, hpPayment = false): number {
    const state = run.value
    if (!state || amount <= 0) return 0
    // 💀 Sudden Death week: every wound costs exactly 1.
    amount = suddenDeathClamp(state, amount)
    // 🎓 v7.20: the practice cannot be lost - a wound stops at 1 ❤️, so a
    // string of mistakes never ends on a defeat screen with no way on.
    if (state.mode === 'tutorial') amount = Math.min(amount, Math.max(0, state.hp - 1))
    state.hp = Math.max(0, state.hp - amount)
    actLogFor(state).damageTaken += amount
    // Per-encounter tally — the puzzle node's "score".
    if (state.encounter) state.encounter.damageTaken = (state.encounter.damageTaken ?? 0) + amount
    if (state.hp > 0 && !hpPayment) starweaveAfterDamage(state)
    if (state.hp <= 0) if (!applyRekindle(state) && (hpPayment || !starweaveAfterDamage(state))) {
      state.defeated = true
      // 🎓 v7.39 (per Javi): the funnel's first campaign defeat.
      if (funnelCampaign(state)) track('first_defeat', { once: true })
      // 📊 v5.64: a fallen run banks its lifetime loss (never in
      // Puzzle Game sessions — damage there is only the score).
      if (typeof state.puzzleLevel !== 'number') {
        useLifetimeStats().recordRunEnd(statsModeOf(state), 'lost', state.playtimeMs ?? 0, state.ascension ?? 0, { act: state.act })
      }
    }
    return amount
  }

  /** The current act's stat log (for the between-acts summary). */
  function actLogFor(state: RunState) {
    if (!state.actLog[state.act]) {
      state.actLog[state.act] = {
        successes: 0,
        failures: 0,
        expiries: 0,
        damageTaken: 0,
        goldEarned: 0,
        dispatches: {},
      }
    }
    return state.actLog[state.act]!
  }

  /**
   * What this mission will cost or grant when it resolves — used both to
   * apply the outcome and to show it in the result overlay. Shared with
   * the rarity simulation via computeMissionOutcome.
   */
  function previewOutcome(mission: Mission) {
    return computeMissionOutcome(run.value, mission)
  }

  /**
   * Puzzle nodes end when every trial is resolved, win or lose. 🧩 v7.77
   * (per Javi): finding the intended split earns a relic pick (judged at
   * Begin the Trials, whatever the damage); otherwise reward quality
   * follows the "score" - total damage taken during the node.
   */
  function finishPuzzleNode(state: RunState, enc: NonNullable<RunState['encounter']>, lastMission: Mission) {
    // 🔁 Rootstock / Avarice / Understudy (v2.85).
    onNodeComplete(state)
    enc.status = 'complete'
    for (const creature of state.roster) {
      if (creature.assignedMissionId !== null && creature.surgePrimed) {
        creature.surgePrimed = false
        creature.surgeCooldown = hasTrainedSkill(creature, 'rallying-cry') ? 0 : TUNING.skills.cooldownSeconds
      }
      // 🧘😴 v6.66: a puzzle node ends without a walk home — a creature
      // that was OUT still spends the bank its trip paid for.
      if (creature.assignedMissionId !== null && !keepsRestAfterMission(creature)) creature.restSeconds = 0
      creature.available = true
      creature.assignedMissionId = null
    }
    enc.missions = enc.missions.filter(m => m.id === lastMission.id)
    enc.returnTrips = []
    // 🧩 Puzzle Game sessions (v1.28): no rewards — the damage tally IS
    // the outcome; the encounter page books the score and heads home.
    if (typeof state.puzzleLevel === 'number') {
      state.pendingRewardPools = []
      state.pendingRewards = null
      return
    }
    const score = enc.damageTaken ?? 0
    // 🧩 v7.77 (per Javi: "I had the split but took damage, so I got no relic"): the relic follows the SPLIT -
    // the excess and the wheel can still bite a perfect split. Not a scratch still earns it too.
    const relic = enc.puzzleSplitMatched === true || score === 0
    enc.puzzleRelic = relic
    state.pendingRewardPools = relic ? ['relic'] : ['regular']
    const pool = state.pendingRewardPools.shift()
    const floor = relic || score <= state.act * 4 ? 'Rare' : null
    // 🦴 v5.57: daily puzzle rewards are skeleton — same key shape as
    // advanceRewardStage, so pool (earned by YOUR score) picks the stream.
    state.pendingRewards = pool
      ? withRunStream(state, `offers/${state.activeNodeId ?? 'x'}/${pool}`, () =>
          generateOffers(pool, state, floor))
      : null
  }

  /** Advances the game clock by dt seconds. */
  function tick(dt: number) {
    const state = run.value
    const enc = state?.encounter
    const cfg = nodeConfig()
    if (!state || !enc || !cfg || enc.status !== 'active') return

    // ⏫ Node age drives the spawn-time damage escalation (v4.72).
    enc.secondsElapsed = (enc.secondsElapsed ?? 0) + dt
    // v4.75/76: the SEVENTH regular spawn arms the enrage (gold and
    // training never count — they ride their own counters). Checked
    // every tick (not at the spawn itself) so mid-camp saves re-arm.
    // 🎓 v7.38: never in the practice - its lesson retries count as
    // arrivals too, and a kid on a third try is not camping.
    // 🎓 v7.41 (per Javi, D7): a first run's act-1 rows 0-1 arm at the 15th.
    if (state.mode !== 'tutorial' && enc.enrageAt === undefined && enc.spawnCount >= enrageArmsAt(state, activeNode.value)) {
      enc.enrageAt = enc.secondsElapsed
    }

    // Surge/trained skill cooldowns tick on the encounter clock.
    for (const creature of state.roster) {
      if (creature.surgeCooldown > 0) creature.surgeCooldown = Math.max(0, creature.surgeCooldown - dt)
      if ((creature.equalizeCooldown ?? 0) > 0) {
        creature.equalizeCooldown = Math.max(0, (creature.equalizeCooldown ?? 0) - dt)
      }
      for (const [id, left] of Object.entries(creature.armedSkillCooldowns ?? {})) {
        if (left > 0) creature.armedSkillCooldowns![id] = Math.max(0, left - dt)
      }
    }
    // 📣 v7.62 (per Javi): a Rallying Cry holder's Surge arms ITSELF - it never
    // cools, so the press was a formality, and now every mission it joins
    // rallies the team. Never under Grueling (no surges there at all).
    if (!nodeHasMod('grueling')) {
      for (const creature of state.roster) {
        if (creature.available && !creature.surgePrimed && creature.surgeCooldown <= 0 && hasTrainedSkill(creature, 'rallying-cry')) creature.surgePrimed = true
      }
    }

    // ⚡ v4.89 (perf review #3): identical for every mission — hoisted
    // out of the loop (it was 48 passive scans per tick).
    const expiryMult = nightWatchExpiryMult(state.roster)
    for (const mission of [...enc.missions]) {
      if (mission.status === 'waiting') {
        // Unstable bosses grow on the field clock too (v0.50).
        tickUnstable(mission, dt)
        // 😡 v5.42 Enraged (elite modifier): a waiting mission grows +1
        // to one posted requirement every ENRAGE_SECONDS, up to the cap.
        if (nodeHasMod('enraged') && !mission.training) {
          mission.enrageTick = (mission.enrageTick ?? 0) + dt
          if (mission.enrageTick >= ENRAGE_SECONDS && (mission.enrageBonus ?? 0) < ENRAGE_CAP) {
            mission.enrageTick = 0
            const posted = ELEMENTS.filter(e => mission.required[e] > 0)
            const grow = (posted.length > 0 ? posted : [...ELEMENTS])[
              Math.floor(Math.random() * (posted.length > 0 ? posted.length : ELEMENTS.length))]!
            mission.required[grow] += 1
            mission.enrageBonus = (mission.enrageBonus ?? 0) + 1
          }
        }
        // 🌫️ Fog of War week: the fog burns off while the mission waits.
        if ((mission.fogRemaining ?? 0) > 0) {
          mission.fogRemaining = Math.max(0, (mission.fogRemaining ?? 0) - dt)
        }
        // 🪨 v5.95: an at-camp Legacy (tier 3+) slows every fuse.
        mission.expireTimer -= dt * expiryMult * steadyGroundExpiryMult(state)
        if (mission.expireTimer <= 0) {
          mission.expireTimer = 0
          mission.status = 'expired'
          if (state.mode === 'tutorial' && enc.tutorialMissionId === mission.id) enc.tutorialOutcome = 'expired' // 🎓 v7.20
          // 📊 v5.64: an unserved mission is a lifetime expiry.
          if (typeof state.puzzleLevel !== 'number' && state.mode !== 'tutorial' && !mission.training) useLifetimeStats().recordMission('expired')
          mission.removeIn = TUNING.encounters.cardLinger
          // 💢 v3.81: what the player actually loses, for the board's float.
          // Stays 0 unless HP really moves — a gold lapse, a Cremation-spared
          // one and a Safety-Netted one all cost nothing and must SHOW
          // nothing (they used to flash the full number and play the hit).
          mission.expiryDamage = 0
          onMissionExpired(state, mission)
          // Gold missions are pure opportunity: letting one go is free.
          if (mission.goldReward) continue
          actLogFor(state).expiries++
          // Expired missions deal reduced damage (§14) — Cremation spares
          // it, camped Stoneguards chip it (v4.16), Safety Net absorbs it.
          // 🗡️ v6.04 No Quarter (per Javi): an expiry bites half
          // again as hard — you cannot triage, so every miss truly hurts.
          const rawExpiry = Math.round(expiryDamageFor(mission.damage, state.roster, state)
            * (nodeHasMod('noquarter') ? 1.5 : 1))
          if (rawExpiry > 0 && safetyNetAvailable(state)) {
            enc.safetyNetUsed = true
          } else {
            // 🧊 v5.15: a watching Warden's pool eats the chip first —
            // the number shown is what actually reached the hearts.
            // 💀 Sudden Death: the float shows the clamped truth too.
            const expiryDamage = suddenDeathClamp(state,
              activeNode.value?.type === 'puzzle' ? rawExpiry : permafrostAbsorb(state, rawExpiry))
            const hpBefore = state.hp // 🎓 v7.41: the kind defeat's tally books what left the hearts
            // 🎓 v7.40 (review): what REALLY landed - at the practice's
            // 1-health floor "You lost 2 health." was false.
            mission.expiryDamage = damagePlayer(expiryDamage)
            tallyFirstRunDamage(state, 'expired', hpBefore, mission.expiryDamage, mission)
          }
          // ⏳ v3.72: a lapsed Amalgam reforms at full strength on a fresh
          // fuse, exactly as Basalt does — the node stays winnable, but
          // camping it costs a boss-sized expiry bite every four minutes.
          if (mission.bossSkill === 'scientist' && mission.tier === 'boss' && !mission.minion) {
            for (const creature of state.roster) {
              if (creature.assignedMissionId === mission.id) {
                creature.available = true
                creature.assignedMissionId = null
              }
            }
            enc.missions.push({
              ...mission,
              id: makeId('m'),
              required: { ...mission.required },
              status: 'waiting',
              assignedCreatureIds: [],
              assignedChance: null,
              expireTimer: SCIENTIST_EXPIRE_SECONDS,
              expireTotal: SCIENTIST_EXPIRE_SECONDS,
              fightTimer: mission.fightTotal,
              travelRemaining: 0,
              removeIn: null,
            })
          }
          // Expired boss splits reform with a fresh timer. A team locked
          // in for the coordinated strike (v0.89) is released — the
          // reformed piece must be locked again.
          if (isBossSplit(mission)) {
            for (const creature of state.roster) {
              if (creature.assignedMissionId === mission.id) {
                creature.available = true
                creature.assignedMissionId = null
              }
            }
            enc.missions.push({
              ...mission,
              id: makeId('m'),
              required: { ...mission.required },
              status: 'waiting',
              assignedCreatureIds: [],
              assignedChance: null,
              expireTimer: FRACTURE_SPLIT_EXPIRE_SECONDS,
              expireTotal: FRACTURE_SPLIT_EXPIRE_SECONDS,
              fightTimer: mission.fightTotal,
              travelRemaining: 0,
              removeIn: null,
            })
          }
          // 🦈 v5.42 Voracious (elite modifier): the lapsed mission
          // RISES once, hungrier. Boss machinery reforms above handle
          // their own kinds; gold missions never reach here (their
          // lapse `continue`s out as pure opportunity).
          if (nodeHasMod('voracious') && voraciousEligible(mission)) {
            enc.missions.push(voraciousRise(mission))
            // 🦈 v6.00 (per Javi): it rises as TWO — the twin claims
            // its own ground when the field has any to give.
            const twin = voraciousRise(mission)
            const spot = rollPosition(false)
            if (spot) {
              twin.x = spot.x
              twin.y = spot.y
              twin.poi = spot.poi
              twin.distance = travelDistance(spot.x, spot.y, spot.poi)
              enc.missions.push(twin)
            }
          }
        }
      } else if (mission.status === 'traveling') {
        mission.travelRemaining -= dt
        if (mission.travelRemaining <= 0) {
          mission.travelRemaining = 0
          mission.status = 'assigned'
        }
      } else if (mission.status === 'assigned') {
        mission.fightTimer -= dt
        if (mission.fightTimer <= 0) {
          mission.fightTimer = 0
          mission.status = 'resolving'
        }
      } else if (mission.removeIn !== null) {
        mission.removeIn -= dt
        if (mission.removeIn <= 0) enc.missions.splice(enc.missions.indexOf(mission), 1)
      }
    }

    // Teams walking home free up when they arrive; an armed surge is
    // spent by the trip and its cooldown starts now.
    for (const trip of [...enc.returnTrips].sort((a, b) => a.remaining - b.remaining)) {
      trip.remaining -= dt
      if (trip.remaining <= 0) {
        if (trip.starweaveFailed) starweaveOnFailedReturn(state, trip.creatureIds)
        for (const creature of state.roster) {
          if (trip.creatureIds.includes(creature.id)) {
            creature.available = true
            creature.assignedMissionId = null
            // 🧘😴 v6.66: home again — the rest bank the trip spent starts
            // over. Deep Sit's Apotheosis holds its buff for the node.
            if (!keepsRestAfterMission(creature)) creature.restSeconds = 0
            if (creature.surgePrimed) {
              state.surgesFired = (state.surgesFired ?? 0) + 1 // 🧘 v6.54
              // 🌊 Tidemother's spent cast layers up for the node (v4.66).
              bankTideStack(creature)
              creature.surgePrimed = false
              // 📣 A rallied teammate's cooldown is waived (v0.92).
              creature.surgeCooldown = hasTrainedSkill(creature, 'rallying-cry') || creature.surgeRallied
                ? 0
                : TUNING.skills.cooldownSeconds
            }
            creature.surgeRallied = false
            // 📣 v7.62: the crier comes home armed again (see tick).
            if (!nodeHasMod('grueling') && hasTrainedSkill(creature, 'rallying-cry')) creature.surgePrimed = true
          }
        }
        enc.returnTrips.splice(enc.returnTrips.indexOf(trip), 1)
      }
    }

    // Gold missions and boss minions don't count against the combat cap.
    // With expiry OFF the cap is goal-relative (goal + capOverGoal), so
    // every slot matters; bosses keep their single-mission cap.
    const liveCount = enc.missions.filter(m =>
      !m.goldReward && !m.minion && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
    ).length
    const fieldCap = !TUNING.encounters.expiryEnabled && activeNode.value?.type !== 'boss'
      ? Math.max(1, enc.goal + TUNING.encounters.capOverGoal)
      : cfg.maxActive
    // 🎓 v7.38 (F04): the practice's field belongs to its script - the
    // clocks are null there (normalizeRun pins them on load), and the
    // random spawners check the mode anyway, like the gold one checks the node.
    const scripted = state.mode === 'tutorial'
    if (!scripted && enc.nextSpawnIn !== null && (fieldCap === 0 || liveCount < fieldCap)) {
      enc.nextSpawnIn -= dt
      if (enc.nextSpawnIn <= 0) {
        const node = activeNode.value
        // 🔢 v7.14 (code review): the arrival counter advances only when a
        // mission actually lands. It bumped on every ATTEMPT, so a full
        // field (five missions plus a gold one or a stationed Robodude on
        // a six-site forest) walked it up every 2 s toward the enrage
        // threshold and skipped seeded daily indices.
        const mission = node ? spawnMission(node, enc.spawnCount) : null
        if (mission) {
          enc.spawnCount++
          enc.missions.push(mission)
          // Opening wave arrives at a quick stagger, then the normal pace.
          // Later acts spawn faster (act pressure ramp).
          const [min, max] = enc.spawnCount < cfg.initialSpawns
            ? TUNING.encounters.openingSpawnEvery
            : cfg.spawnEvery
          const scale = TUNING.acts.scaling[Math.min(state.act, TUNING.acts.scaling.length) - 1]!
          // 🌊 The Flood week: arrivals come half again as fast.
          enc.nextSpawnIn = randInt(min, max) * scale.spawnMult * weeklySpawnMult(state.weeklyMod)
          // 🚩 v6.00 (per Javi): the champion marches in second, with
          // another AT ITS HEELS — the third arrival gives no breather.
          if (nodeHasMod('banner') && enc.spawnCount === 2) enc.nextSpawnIn = 1
        } else {
          // Field is full — retry once a zone frees up.
          enc.nextSpawnIn = 2
        }
      }
    }

    // Boss minions: Dr. Vex keeps meddling (1 at a time); Basalt keeps
    // a PENTAGRAM of five crystals around it (v0.61) — smashed ones
    // reform seconds later with freshly rolled stats. No goal credit.
    const bossSkill = activeNode.value?.preview?.boss?.skill
    if ((bossSkill === 'scientist' || bossSkill === 'golem') && typeof enc.nextMinionSpawnIn === 'number') {
      const cap = bossSkill === 'scientist' ? 1 : GOLEM_CRYSTAL_CAP
      const liveMinions = enc.missions.filter(m =>
        m.minion && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
      )
      if (liveMinions.length < cap) {
        enc.nextMinionSpawnIn -= dt
        if (enc.nextMinionSpawnIn <= 0) {
          const position: { x: number; y: number; poi?: number } | null = bossSkill === 'golem'
            ? freePentagramSpot(liveMinions)
            : rollPosition(false)
          if (position) {
            const minion = generateMinion(bossSkill === 'scientist' ? 'scientist' : 'crystal', state.act)
            minion.x = position.x
            minion.y = position.y
            minion.poi = position.poi
            minion.distance = travelDistance(position.x, position.y, position.poi)
            enc.missions.push(minion)
          }
          // The ring fills instantly; smashed crystals take a beat —
          // longer once a fight has been lost (v0.62). Dr. Vex's
          // meddling reappears fast (v0.63).
          enc.nextMinionSpawnIn = bossSkill === 'golem'
            ? (liveMinions.length + 1 < cap
                ? 0
                : enc.golemSlowed ? GOLEM_CRYSTAL_RESPAWN_SLOW_SECONDS : GOLEM_CRYSTAL_RESPAWN_SECONDS)
            : SCIENTIST_MEDDLE_RESPAWN_SECONDS
        }
      }
    }

    // Gold missions: an independent clock — one can appear right
    // alongside a regular spawn. Success pays gold and never counts
    // toward the node goal; letting one expire is harmless. Defense in
    // depth (v0.88, the Yin & Yang bug): the clock is null on boss and
    // puzzle fields, the node types are re-checked here anyway, and a
    // gold tag NEVER lands on a boss-tier roll — a gold "boss clone"
    // once blocked node completion forever.
    const goldNode = activeNode.value
    const goldAllowed = goldNode && goldNode.type !== 'boss' && goldNode.type !== 'puzzle' && !scripted
    const goldLive = enc.missions.filter(m =>
      m.goldReward && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
    ).length
    if (goldAllowed && goldLive < TUNING.gold.maxActive && typeof enc.nextGoldSpawnIn === 'number') {
      enc.nextGoldSpawnIn -= dt
      if (enc.nextGoldSpawnIn <= 0) {
        // 🪙 Own counter (v2.17): gold spawns must NOT eat a slot from the
        // regular spawner's opening-wave stagger or Calm Hourglass's
        // "first 5 missions" window — those index off enc.spawnCount.
        enc.goldSpawnCount = (enc.goldSpawnCount ?? 0) + 1
        // 🦴 v5.57: own lane so a daily's gold spawns never share a
        // stream key with the regular spawner's same-numbered mission.
        const mission = spawnMission(goldNode, enc.goldSpawnCount, 'gold')
        if (mission && mission.tier !== 'boss') {
          mission.goldReward = withRunStream(state, `gold-purse/${goldNode.id}/${enc.goldSpawnCount}`, () => Math.round(
            randInt(TUNING.gold.rewardBase[0], TUNING.gold.rewardBase[1])
            * (TUNING.gold.actMult[Math.min(state.act, TUNING.gold.actMult.length) - 1] ?? 1),
          ))
          enc.missions.push(mission)
          enc.nextGoldSpawnIn = randInt(TUNING.gold.spawnEvery[0], TUNING.gold.spawnEvery[1])
          // Gold glints as it lands (v0.79).
          sfx.goldAppear()
        } else {
          enc.nextGoldSpawnIn = 2
        }
      }
    }

    // 🎓 Training mission (v0.79): once per act, on regular combat nodes.
    tickTrainingSpawn(state, enc, dt)
    tickFirstDispatch(state, enc, dt)

    // 🌳 Deep Roots: home time on regular combat nodes is study time.
    // Bosses/puzzles are excluded — no expiry pressure there means
    // idling would be free XP.
    // 🎯 Creature-skill cooldowns tick alongside the surge clock (v2.86).
    tickCreatureSkills(state.roster, dt)
    tickEncounterBuffs(state, dt)
    // 🕊️ v6.90 The Noble One: quiet seconds bank Silence for the whole team.
    silenceTick(state, dt)
    // 🌌 v6.91 Lanfeust: the board bends toward him while he waits.
    gravityTick(state, dt)
    // 🧘 Deep Sit (v2.82) banks home time for every resting creature —
    // the passive itself decides whether that time is worth anything.
    for (const creature of state.roster) {
      if (creature.available || restsWhileOut(creature)) creature.restSeconds = (creature.restSeconds ?? 0) + dt
    }
    // 🐻 Hibernal Might (v4.66): a completed hibernation banks its
    // permanent stats — once per node, checked every tick.
    for (const creature of state.roster) {
      if (creature.available) hibernalBank(creature)
    }
    const node = activeNode.value
    if (node && node.type !== 'boss' && node.type !== 'puzzle') {
      for (const creature of state.roster) {
        if (!creature.available || !hasTrainedSkill(creature, 'deep-roots')) continue
        creature.deepRootsClock = (creature.deepRootsClock ?? 0) + dt
        // every 2s since v4.65 (per Javi's lose-on-purpose buff; 5s from
        // v1.94, 15 originally)
        while (creature.deepRootsClock >= 2) {
          creature.deepRootsClock -= 2
          grantXp(creature, 1, state)
        }
      }
    }
  }

  /** 🎓 v7.04 First Dispatch: one beat at a time. When nothing is live on the
   * field, the next scripted mission arrives after a short breath; a beat may
   * bring a creature with it (Rillhopper, for the pair and skill lessons). */
  function tickFirstDispatch(state: RunState, enc: NonNullable<RunState['encounter']>, dt: number) {
    if (state.mode !== 'tutorial' || enc.status !== 'active') return
    if (enc.missions.some(m => ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status))) return
    // 🎓 v7.20 (tutorial review): the lesson just dealt has ended - did it
    // teach? A required lesson ('dispatch', 'pair') advances only on a
    // win; a discard, an expiry or a loss deals it again, with the "try
    // again" coach. The 'expire' lesson resolves either way. The script
    // used to run out after three deals, leaving a 1/2 goal with no
    // mission, no card and no way forward.
    if (enc.tutorialMissionId) {
      const dealt = FIRST_DISPATCH.script[(enc.tutorialStep ?? 1) - 1]
      const won = enc.tutorialOutcome === 'won'
      enc.tutorialMissionId = undefined
      enc.tutorialOutcome = undefined
      if (dealt && dealt.lesson !== 'expire' && !won) {
        enc.tutorialStep = Math.max(0, (enc.tutorialStep ?? 1) - 1)
        enc.tutorialRetries = (enc.tutorialRetries ?? 0) + 1
      } else {
        enc.tutorialRetries = 0
        // 🎓 v7.39 (per Javi): the script moves past lesson 1 (won) or lesson 2
        // (the expiry, resolved either way) - count the funnel step.
        if (dealt && funnelPractice(state)) {
          if (dealt.lesson === 'dispatch') track('practice_lesson1_won', { once: true })
          else if (dealt.lesson === 'expire') track('practice_lesson2_done', { once: true })
        }
      }
    }
    // 🎓 v7.38: a practice whose script ran out short of its goal (a
    // v7.04-v7.19 save moved on past a lost lesson) deals its last taught
    // lesson again - the field used to stay empty for good.
    if (!FIRST_DISPATCH.script[enc.tutorialStep ?? 0] && enc.successes < enc.goal) {
      let last = FIRST_DISPATCH.script.length - 1
      while (last > 0 && FIRST_DISPATCH.script[last]!.lesson === 'expire') last--
      enc.tutorialStep = last
    }
    const beat = FIRST_DISPATCH.script[enc.tutorialStep ?? 0]
    const node = activeNode.value
    if (!beat || !node) return
    // 🎓 v8.02 (review): a lesson waits for everyone to get HOME. The surge,
    // pair and skill beats follow one another with no 7 s lapse between, a
    // walk home lasts 3.5 s or more, and a sheet opened on a walking creature
    // freezes its walk (the clock pauses with the sheet up) - it could never
    // board. A Skim trip is 0 s and clears on the next tick.
    if (enc.returnTrips.length > 0) { enc.tutorialClock = FIRST_DISPATCH.beatGap; return }
    enc.tutorialClock = (enc.tutorialClock ?? FIRST_DISPATCH.beatGap) - dt
    if (enc.tutorialClock > 0) return
    if (beat.join && !state.roster.some(c => c.defId === beat.join)) {
      state.roster.push(makeCreature(getCreatureDef(beat.join)))
    }
    // 🎓 v7.38 (per Javi): the lesson is GENERATED from its own asks, so
    // the name, the art and the codex sighting (all read at spawn) wear
    // the lesson's element - a ⚡ lesson used to show fire imps.
    const mission = spawnMission(node, enc.spawnCount, 'mission', beat.required, beat.enemy)
    if (!mission) { enc.tutorialClock = 1; return }
    enc.spawnCount++
    mission.required = { ...emptyStats(), ...beat.required }
    mission.slots = beat.slots
    mission.tier = 'easy'
    mission.expireTimer = beat.fuse
    mission.expireTotal = beat.fuse
    mission.damage = beat.damage
    mission.xp = beat.xp
    mission.fogRemaining = undefined
    mission.tutorialLesson = beat.lesson
    enc.tutorialMissionId = mission.id
    enc.missions.push(mission)
    enc.tutorialStep = (enc.tutorialStep ?? 0) + 1
    enc.tutorialClock = FIRST_DISPATCH.beatGap
  }
  /** Once per act a Training Grounds mission appears on the act's FIRST
   * regular combat node, right behind the node's first mission — the
   * second spawn (v0.80, per Javi; was a mid-node chance roll).
   * Bosses/puzzles stay clean. */
  function tickTrainingSpawn(state: RunState, enc: NonNullable<RunState['encounter']>, dt: number) {
    if (state.mode === 'tutorial') return // 🎓 v7.04: the First Dispatch deals its own beats
    // 🎓 Ascension-only (v2.30, per Javi): normal runs keep the base
    // kit — the Training Grounds (and its tutorial tip, which only
    // fires when one lands) start at Ascension 1.
    if ((state.ascension ?? 0) < 1) return
    const node = activeNode.value
    if (!node || node.type === 'boss' || node.type === 'puzzle') return
    if ((state.trainingActs ?? []).includes(state.act)) return
    if (enc.missions.some(m => m.training)) return
    // Hold until the node's first mission has landed…
    if (!enc.missions.some(m => !m.training)) return
    // …then arrive a beat behind it as the second spawn.
    trainingDelay.value -= dt
    if (trainingDelay.value > 0) return
    const position = rollPosition(false)
    if (!position) {
      trainingDelay.value = 2
      return
    }
    // 🧩 v3.19 (per Javi's audit): the school teaches only what the
    // roster does NOT already know — same rule as the reward card and
    // the shop special (teachableSkills), synergy nudge included.
    const options = rollTrainingOptions(state, { mission: true })
    // 👁️ v6.03: the curriculum on display is a sighting.
    for (const id of options) useCollection().record('skills', id, { announce: false })
    if (options.length === 0) return
    const mission = generateTrainingMission(options)
    mission.x = position.x
    mission.y = position.y
    mission.poi = position.poi
    mission.distance = travelDistance(position.x, position.y, position.poi)
    enc.missions.push(mission)
    state.trainingActs = [...(state.trainingActs ?? []), state.act]
    // The grand entrance: a bigger glow and a grander chime.
    sfx.trainingAppear()
  }

  /** Draws `n` distinct random ids (the Sandman's lullaby lottery). */
  function pickRandomIds(ids: string[], n: number): string[] {
    const pool = [...ids]
    const out: string[] = []
    while (out.length < n && pool.length > 0) {
      out.push(pool.splice(randInt(0, pool.length - 1), 1)[0]!)
    }
    return out
  }

  function openMission(missionId: string) {
    const mission = encounter.value?.missions.find(m => m.id === missionId)
    if (mission?.status === 'waiting') selectedMissionId.value = missionId
  }

  function closeMission() {
    selectedMissionId.value = null
  }

  /**
   * Discard a waiting mission (v0.56): pay the expiry damage to clear
   * the slot so a fresh mission can spawn. Walking away from gold is
   * free; boss missions and minions can't be discarded.
   */
  function discardMission(missionId: string) {
    const state = run.value
    const enc = state?.encounter
    const mission = enc?.missions.find(m => m.id === missionId)
    if (!state || !enc || !mission || mission.status !== 'waiting') return
    if (mission.tier === 'boss' || mission.minion) return
    // ⛓️ v5.43 No Quarter (elite modifier): no discarding on this ground.
    if (nodeHasMod('noquarter')) return
    // The board's expiry watcher supplies the hit if health is lost; no cancellation cue on trash.
    // 🪨 v7.77 (Stoneguard review: the button said 4, the player paid 1): price the discard FIRST, exactly as
    // its button does - before the trial's locked team is freed (a guard locked into it would count as camped)
    // and before the expiry hooks run (Tough Learner's XP could wake a dormant guard mid-discard).
    const price = expiryDamageFor(mission.damage, state.roster, state)
    // A discarded puzzle trial frees its locked-in team (v0.63).
    for (const creature of state.roster) {
      if (creature.assignedMissionId === mission.id) {
        creature.available = true
        creature.assignedMissionId = null
      }
    }
    mission.assignedCreatureIds = []
    mission.status = 'expired'
    if (state.mode === 'tutorial' && enc.tutorialMissionId === mission.id) enc.tutorialOutcome = 'discarded' // 🎓 v7.20
    // 📊 v5.64: a discard is an expiry in the lifetime ledger too —
    // same as the damage accounting has treated it since v5.26.
    if (typeof state.puzzleLevel !== 'number' && state.mode !== 'tutorial' && !mission.training) useLifetimeStats().recordMission('expired')
    mission.removeIn = TUNING.encounters.cardLinger
    mission.expiryDamage = 0
    onMissionExpired(state, mission)
    selectedMissionId.value = null
    if (!mission.goldReward) {
      actLogFor(state).expiries++
      const dmg = Math.round(price * (nodeHasMod('noquarter') ? 1.5 : 1))
      if (dmg > 0 && safetyNetAvailable(state)) enc.safetyNetUsed = true
      else {
        // 🧊 v5.26 (per Javi: "is that considered an expired mission? it
        // should"): the trash button IS an expiry — the Warden absorbs
        // it like any other. v5.15 kept discards un-absorbed to block
        // freeze-farming, but v5.21's per-node pool caps that anyway.
        const landed = suddenDeathClamp(state,
          activeNode.value?.type === 'puzzle' ? dmg : permafrostAbsorb(state, dmg))
        const hpBefore = state.hp // 🎓 v7.41: the kind defeat's tally books what left the hearts
        tallyFirstRunDamage(state, 'discarded', hpBefore, damagePlayer(landed), mission)
        mission.expiryDamage = landed
      }
    }
    // Discarding the last puzzle trial also ends the node.
    if (mission.puzzle) {
      const anyLive = enc.missions.some(m =>
        m.id !== mission.id && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
      )
      if (!anyLive) finishPuzzleNode(state, enc, mission)
    }
  }

  /**
   * Plan-then-commit locking (v0.63 puzzles; v0.89 boss splits too, per
   * Javi): teams are LOCKED IN per trial/piece, nothing moves.
   * Committed creatures leave the pool; passing an empty list clears
   * the team. Split pieces keep their expire fuses BURNING while locked
   * — plan fast or they reform.
   */
  function planPuzzleTrial(missionId: string, creatureIds: string[]) {
    const state = run.value
    const enc = state?.encounter
    const mission = enc?.missions.find(m => m.id === missionId)
    if (!state || !enc || !mission || mission.status !== 'waiting') return
    if (!mission.puzzle && !isBossSplit(mission)) return
    // Free this mission's previous team first, then commit the new one.
    for (const creature of state.roster) {
      if (creature.assignedMissionId === mission.id) {
        creature.available = true
        creature.assignedMissionId = null
      }
    }
    const team = creatureIds
      .map(id => state.roster.find(c => c.id === id))
      .filter((c): c is NonNullable<typeof c> => !!c && c.available && !c.benched && !((c.benchSeconds ?? 0) > 0))
    mission.assignedCreatureIds = team.map(c => c.id)
    for (const creature of team) {
      creature.available = false
      creature.assignedMissionId = mission.id
    }
    // Marker badge: the planned chance, kept live until the strike.
    // Boss pieces read raw coverage (§18); trials use the normal curve.
    mission.assignedChance = team.length === 0
      ? null
      : mission.tier === 'boss'
        ? curseAdjustedChance(
            mission,
            Math.round(coverage(mission.required, missionTeamStats(mission, team, state)) * 100),
            team.length,
          )
        : Math.min(100, successChance(
            mission.required,
            missionTeamStats(mission, team, state),
            state,
            undefined, // v3.33: act-faded exponent, derived from state
            team.length,
          ) + passiveChanceBonus(team, state, mission))
    // ⚖️ v4.31 (Javi's Yin & Yang report): every locked twin's badge
    // refreshes against the PREDICTED post-transfer bars, so the number
    // you lock is the number the wheel spins.
    refreshSplitBadges(state, enc)
    selectedMissionId.value = null
  }

  /** Recompute every locked yin/yang piece's badge with the simulated
   * Strike-as-One transfers (deterministic since v4.31). */
  function refreshSplitBadges(state: RunState, enc: NonNullable<RunState['encounter']>) {
    const pieces = enc.missions
      .filter(m => isBossSplit(m) && m.bossSkill === 'yinyang' && m.status === 'waiting')
      .map(m => ({
        mission: m,
        team: m.assignedCreatureIds
          .map(id => state.roster.find(c => c.id === id))
          .filter((c): c is NonNullable<typeof c> => !!c),
      }))
    if (pieces.length < 2) return
    const predicted = predictSplitRequirements(pieces, state)
    for (const { mission, team } of pieces) {
      if (team.length === 0) continue
      mission.assignedChance = curseAdjustedChance(
        mission,
        Math.round(coverage(predicted[mission.id]!, missionTeamStats(mission, team, state)) * 100),
        team.length,
      )
    }
  }

  /**
   * The big red button (v0.63): once EVERY trial has a locked-in team,
   * the whole gauntlet resolves at once — no travel, no fight timers,
   * just staggered verdicts.
   */
  function beginPuzzleTrials() {
    const state = run.value
    const enc = state?.encounter
    if (!state || !enc || enc.status !== 'active') return
    const trials = enc.missions.filter(m => m.puzzle && m.status === 'waiting')
    if (trials.length === 0 || trials.some(m => m.assignedCreatureIds.length === 0)) return
    // 🧩 v7.77: the split is judged as it locks in (the trials' cards leave the board as they resolve).
    enc.puzzleSplitMatched = trials.every(m => puzzleTeamMatchesAnswer(m.assignedCreatureIds, m.puzzleAnswer ?? [], state.roster))
    trials.forEach((mission, index) => {
      const ids = [...mission.assignedCreatureIds]
      // Hand the team back so startFight can re-validate and commit it.
      for (const id of ids) {
        const creature = state.roster.find(c => c.id === id)
        if (creature) {
          creature.available = true
          creature.assignedMissionId = null
        }
      }
      mission.assignedCreatureIds = []
      startFight(mission.id, ids)
      // Trials fight where they stand: no travel, verdicts land one
      // after another.
      mission.status = 'assigned'
      mission.travelTotal = 0
      mission.travelRemaining = 0
      const fight = 0.01 + index * 1.2
      mission.fightTimer = fight
      mission.fightTotal = fight
    })
  }

  /**
   * ⚔️ Strike as One (v0.89, per Javi): split-boss pieces (Myriad's
   * fragments, Yin & Yang's halves) can only be fought TOGETHER — lock
   * a team into every waiting piece, then this dispatches them all.
   * Unlike puzzle trials the pieces keep their travel and fight timers
   * (dispatch commits, so the fuses stop at the strike).
   */
  function beginSplitStrike() {
    const state = run.value
    const enc = state?.encounter
    if (!state || !enc || enc.status !== 'active') return
    const pieces = enc.missions.filter(m => isBossSplit(m) && m.status === 'waiting')
    if (pieces.length === 0 || pieces.some(m => m.assignedCreatureIds.length === 0)) return
    for (const mission of pieces) {
      const ids = [...mission.assignedCreatureIds]
      // Hand the team back so startFight can re-validate and commit it.
      for (const id of ids) {
        const creature = state.roster.find(c => c.id === id)
        if (creature) {
          creature.available = true
          creature.assignedMissionId = null
        }
      }
      mission.assignedCreatureIds = []
      startFight(mission.id, ids)
    }
  }

  /**
   * onMissionAssigned hook: dispatch commits — the expire timer stops and
   * the team starts walking at its slowest member's pace. The fight timer
   * (with its relics) runs once they arrive.
   */
  function startFight(missionId: string, creatureIds: string[]) {
    const state = run.value
    const enc = state?.encounter
    const mission = enc?.missions.find(m => m.id === missionId)
    if (!state || !enc || !mission || mission.status !== 'waiting' || creatureIds.length === 0) return
    // Pick order is preserved — Cursed Slot punishes the first pick.
    // Puzzle trials: each creature may join only ONE trial per node.
    const team = creatureIds
      .map(id => state.roster.find(c => c.id === id))
      .filter((c): c is NonNullable<typeof c> =>
        !!c && c.available && !knockedOutIds(mission).includes(c.id)
        && !(mission.puzzle && enc.everAssignedIds.includes(c.id)),
      )
    if (team.length === 0) return
    // 🎓 Training: exactly one trainee, and a skill must be chosen.
    // 🎓 v4.16: only the TRAINEE must not know the skill — another
    // creature knowing it no longer blocks the class (duplicates
    // allowed, one copy per creature).
    if (mission.training && (team.length !== 1 || !mission.trainingChoice || !canLearnSkill(team[0]!, mission.trainingChoice))) return
    // 🎓 v6.85 Broader Curriculum: a second lesson rides along only while the
    // run's double lesson is unspent and the student can learn it too.
    if (mission.training && mission.trainingSecondChoice && (
      mission.trainingSecondChoice === mission.trainingChoice || !canStarweaveDoubleTraining(state, mission.id)
      || !(mission.trainingOptions ?? []).includes(mission.trainingSecondChoice) || !canLearnSkill(team[0]!, mission.trainingSecondChoice)
    )) mission.trainingSecondChoice = null

    // 🎲 v6.88: Mr. Khool's armed Wager rides THIS mission (stamped so a
    // reload cannot lose the bet; the primed flag itself is consumed at
    // resolution like every other primed skill).
    // 🎲 v7.79: decided at EVERY dispatch - a reformed mission (a lost boss piece, the Sandman, Basalt, a Voracious
    // rise: they copy ...mission) used to carry the old stamp, so the next attempt rode a bet nobody placed.
    if (team.some(c => c.defId === 'mr-khool' && c.skillPrimed && creatureOwnSkill(c)?.id === 'the-wager')) mission.wagered = true
    else delete mission.wagered
    // 🌀 v6.91: Lanfeust's armed Blackhole swallows an eligible dispatch (stamped like a decree).
    if (blackholeArmed(team, mission)) mission.eaten = true
    // Boss Feint fires the moment the player commits: the telegraphed
    // requirement scatters onto the other elements before the roll.
    applyFeint(mission)

    // The route is laid out now, when the team sets off. 🏟️ v6.28: from
    // the field's own trailhead (the arena rim, the wide bridge) rather
    // than the classic bottom-centre, and a short dash bends less — a
    // ±16 swerve on a 14-unit sprint at the boss read as a stagger.
    const origin = fieldHome()
    const midX = (mission.x + origin.x) / 2
    const midY = (mission.y + origin.y) / 2
    const span = Math.hypot((mission.x - origin.x) * X_SCALE, mission.y - origin.y)
    const swerveX = Math.min(16, Math.round(span / 12))
    const swerveY = Math.min(14, Math.round(span / 14))
    mission.ctrlX = Math.min(94, Math.max(6, midX + randInt(-swerveX, swerveX)))
    mission.ctrlY = midY + randInt(-swerveY, swerveY)

    mission.assignedCreatureIds = team.map(c => c.id)
    // Team Journal: a prior failure primes this dispatch with +10% —
    // never wasted on riskless training.
    if (enc.teamJournalPrimed && hasRelic(state, 'relic-team-journal') && !mission.training) {
      mission.journalBoosted = true
      enc.teamJournalPrimed = false
    }
    // 😤 Grudge: a primed grudge-holder aboard cashes it in for +20%.
    if (!mission.training && mission.tier !== 'boss'
      && team.some(c => hasTrainedSkill(c, 'grudge') && c.grudgePrimed)) {
      mission.grudgeBoosted = true
      for (const c of team) if (hasTrainedSkill(c, 'grudge')) c.grudgePrimed = false
    }
    // 📣 Rallying Cry (reworked v0.92, per Javi): SURGING into a mission
    // re-readies everyone else's Surge on the spot — teammates in this
    // very mission included (their post-return cooldown is waived).
    const rallier = team.find(c => hasTrainedSkill(c, 'rallying-cry') && c.surgePrimed)
    if (rallier) {
      for (const creature of state.roster) {
        if (creature.id === rallier.id) continue
        if (team.some(t => t.id === creature.id)) creature.surgeRallied = true
        else creature.surgeCooldown = 0
      }
    }
    // 🩸 Blood Pact: the toggle only sticks when a pact-holder actually
    // ships out (and never on bosses/training). Pay the 2 HP now.
    if (mission.bloodPactPaid) {
      // 🌹 Bloodbloom (v3.05): a Lily counts as a pact-holder, sets the
      // cost (1 HP, team-scaled at T3, free at Apotheosis).
      const pactCost = bloodPactCost(team)
      if (!mission.training && mission.tier !== 'boss'
        && (teamHasTrained(team, 'blood-pact') || bloodbloomAboard(team)) && state.hp > pactCost) {
        if (pactCost > 0) damagePlayer(pactCost, true)
      } else {
        mission.bloodPactPaid = false
      }
    }
    // 🎭 v7.14: a Mime aboard the node's OPENING team wears its copy from
    // this dispatch on (onMissionDispatched -> routineOnDispatch), so the
    // assigned chance is built from the same predicted statline the
    // builder previewed - the number never shifts (the v0.81 promise).
    // 🎭 v7.19 (v7.18 review): the predicted line feeds EVERY pure calculation
    // below - stats, chance AND pacing. The borrowed passive's chance and
    // haste were still read off the raw team, so the assigned odds and the
    // fight time missed what the resolution then counted (40% vs 60%).
    // The mutating hooks (onMissionDispatched etc.) keep the real objects.
    const predicted = withPredictedRoutine(team, state)
    const stats = missionTeamStats(mission, predicted, state)
    // Yin & Yang: overkill against one half strengthens its twin.
    applyOverkillTransfer(mission, stats, enc.missions)
    if (mission.training) {
      // Training can't fail — the time away is the cost.
      mission.assignedChance = 100
    } else if (mission.tier === 'boss') {
      // Boss fights: raw coverage IS the win chance (curses apply).
      mission.assignedChance = curseAdjustedChance(
        mission,
        Math.round(coverage(mission.required, stats) * 100),
        team.length,
      )
    } else {
      const baseChance = successChance(
        mission.required,
        stats,
        state,
        undefined, // v3.33: act-faded exponent, derived from state
        team.length,
      )
      // Shared pipeline (v0.81): the same math the builder previewed and
      // the resolution will use — the number never shifts.
      mission.assignedChance = adjustedTeamChance(baseChance, predicted, {
        journal: !!mission.journalBoosted,
        grudge: !!mission.grudgeBoosted,
        bloodPact: !!mission.bloodPactPaid,
              tithe: !!enc.tithePrimed,
              deluge: (enc.delugeSeconds ?? 0) > 0,
              bloodPactBonus: bloodPactBonus(predicted),
      }, state, mission)
    }
    // 👑 Decree (v3.06) is stamped BEFORE the status flip (v3.56 fix):
    // decreeEligible() requires a mission that is still 'waiting', so
    // running this after the line below meant the royal order NEVER
    // applied — the button said "Decree!", the team walked out normally,
    // and the use was not even spent. Broken since the day it shipped;
    // found by Javi losing a run to it.
    const armedKirin = decreeArmedCreature(state)
    if (armedKirin && decreeEligible(mission) && canDecree(armedKirin)) {
      applyDecree(armedKirin, mission)
      clearDecreeArmed(state)
      state.skillsUsed = (state.skillsUsed ?? 0) + 1 // 🧘 v6.54: the decree lands here
      breakSilence(state) // 🕊️ v6.90: a press is a press
    }
    mission.status = 'traveling'
    // v2.28 (per Javi): bosses no longer skip travel — the team WALKS to
    // the throne ring instead of the fight materializing mid-room ("the
    // boss meets the creatures in the middle"). Bosses have no expire
    // timer, so the walk costs nothing strategically.
    // Fast Deployment: the first team each encounter arrives instantly.
    // Warning Bell: urgent dispatches (<10s left) travel faster.
    // (👑 Decree is applied above, before the status flip.)
    // 🏃 v2.08 pacing: stretched and floored so every leg plays at least
    // one full run-cycle loop (see TUNING.travel). Computed up front so
    // the walk HOME can use it even when the trip out is instant.
    const naturalTravel = Math.max(
      TUNING.travel.minSeconds,
      (mission.distance * TUNING.travel.timeMult) / dispatchSpeed(state, team, mission.expireTimer),
    )
    // 🏗️ v6.92 Robodude's Hold Position: his leg is measured from where he
    // stands, not from camp; a mixed team waits for whoever arrives last.
    // He leaves the site the moment he is dispatched. The walk HOME stays
    // the camp leg (he never walks it anyway).
    let outbound = naturalTravel
    const stationed = team.filter(c => c.stationedPoi !== undefined && c.stationedX !== undefined && c.stationedY !== undefined)
    if (stationed.length) {
      const fromCamp = team.length > stationed.length ? naturalTravel : 0
      const legs = stationed.map((c) => {
        const px = Math.hypot((mission.x - c.stationedX!) * X_SCALE, mission.y - c.stationedY!)
        const distance = Math.min(TUNING.travel.maxDistance, Math.max(TUNING.travel.minDistance, Math.round(px / DISTANCE_SCALE)))
        return Math.max(TUNING.travel.minSeconds, (distance * TUNING.travel.timeMult) / dispatchSpeed(state, [c], mission.expireTimer))
      })
      outbound = Math.max(fromCamp, ...legs)
      for (const c of stationed) { c.stationedPoi = undefined; c.stationedX = undefined; c.stationedY = undefined }
    }
    const blazes = teamHasTrained(team, 'trailblazer')
    const skims = teamSkimsTravel(team)
    if (mission.decreed || mission.eaten || blazes || skims) {
      // 🥾 Trailblazer / 💧 Skim: instant arrival — Fast Deployment's
      // charge is saved for another team.
      mission.travelTotal = 0
      // v3.37 (per Javi): Trailblazer blazes the trip OUT only — its
      // teams walk home at normal pace. 💧 Skim ("out and back"),
      // 👑 Decree ("no travel, no wait") and 🌀 Blackhole still cover both legs.
      mission.homeTravel = blazes && !skims && !mission.decreed && !mission.eaten ? naturalTravel : 0
    } else if (hasRelic(state, 'relic-fast-deployment') && !enc.fastDeployUsed) {
      enc.fastDeployUsed = true
      mission.travelTotal = 0
      mission.homeTravel = 0
    } else {
      mission.travelTotal = outbound
      mission.homeTravel = naturalTravel
    }
    // 🏃 Fresh Legs week: no travel at all — out and back are instant.
    // Danger still scales with the site's DISTANCE; only the walk dies.
    if (hasWeeklyMod(state, 'freshlegs')) {
      mission.travelTotal = 0
      mission.homeTravel = 0
    }
    mission.travelRemaining = mission.travelTotal

    // 🌄 First Light (v3.07 audit fix): the haste half of the passive —
    // "resolves 20% faster" — was never applied. Captured BEFORE
    // onMissionDispatched marks the node's first dispatch done.
    const firstLightHaste = firstLightBonus(predicted, state.roster, enc.firstDispatchDone ?? false).haste
    const { fightTimer, usedFocusBell } = modifiedFightTimer(state, enc.focusBellUses < 3, mission.fightTimer)
    if (usedFocusBell) enc.focusBellUses++
    // ⚔️ Fight stat (v0.91): one summed number covers Storm Tempo,
    // Pacesetter and 💨/⚓ stacks — each point −4%, cap −70%.
    const pacedFight = mission.decreed || mission.eaten
      ? MIN_FIGHT_TIMER
      : Math.max(MIN_FIGHT_TIMER, Math.ceil(fightTimer * teamFightTimeMult(predicted, state) * (1 - firstLightHaste)))
    mission.fightTimer = pacedFight
    mission.fightTotal = pacedFight

    // 🔁 All dispatch-time payouts (Quartermaster, Sundial, Tailwind,
    // First Light) live in battle.ts so the sims see them too (v2.85).
    onMissionDispatched(state, mission, team)

    const log = actLogFor(state)
    for (const creature of team) {
      creature.available = false
      creature.assignedMissionId = mission.id
      // 🧘😴 v6.66 (Javi's report: "I rested him, sent him off on a mission
      // with 100%, but when it came time for the resolution, it was down to
      // 50%"): the banked rest is NOT cleared here. It used to be, which
      // meant Deep Sit's 🪨 and Power Nap's +all — both promised "for that
      // fight" / "on its next trip" — were gone the instant the trip began.
      // The preview and the locked-in badge counted the bonus (they read the
      // creature before dispatch); the wheel recomputes from live stats at
      // resolution and saw nothing, so the shown number fell between the two
      // screens. The bank now rides the trip and is spent when the creature
      // gets HOME (the returnTrips arrival below) — "unbroken rest at home",
      // which is what both passives describe. It cannot grow on the way: the
      // accrual tick requires `available` (or Power Nap's Apotheosis).
      log.dispatches[creature.id] = (log.dispatches[creature.id] ?? 0) + 1
      if (!enc.everAssignedIds.includes(creature.id)) enc.everAssignedIds.push(creature.id)
    }
    selectedMissionId.value = null
  }

  /** 🏗️ v6.92 Robodude's Hold Position: whoever holds position stays at
   * the site (available, blocking it); everyone else walks home. Only a
   * real site can be held - zone-grid spawns and classes send him home. */
  function homewardIds(mission: Mission): string[] {
    const state = run.value
    if (!state) return [...mission.assignedCreatureIds]
    const home: string[] = []
    for (const id of mission.assignedCreatureIds) {
      const creature = state.roster.find(c => c.id === id)
      if (creature && holdsPosition(creature) && mission.poi !== undefined && !mission.training) stationCreature(creature, mission)
      else home.push(id)
    }
    return home
  }

  /** 🎓 Hands the trainee its chosen skill (idempotent). */
  function grantTrainedSkill(state: RunState, mission: Mission) {
    const choice = mission.trainingChoice
    const creature = state.roster.find(c => c.id === mission.assignedCreatureIds[0])
    if (!choice || !creature) return
    // 🎓 v6.85 Broader Curriculum: the second lesson (once per act since v7.69), taught
    // with the first (a repeat call sees it already learned and stays idempotent).
    const secondId = mission.trainingSecondChoice
    const second = secondId && secondId !== choice && !!state.starweave && hasStarweave(state, 'nova-guiding')
      && (mission.trainingOptions ?? []).includes(secondId)
      && (creature.learnedSkills?.includes(secondId) || (state.starweave.curriculumAct !== state.act && canLearnSkill(creature, secondId)))
      ? secondId : null
    // 🔁 v5.17: the field school's unpicked lessons wait out the cycle.
    recordDeclinedOffers(state, 'skill', (mission.trainingOptions ?? []).filter(id => id !== second), choice)
    creature.learnedSkills = creature.learnedSkills ?? []
    if (!creature.learnedSkills.includes(choice)) creature.learnedSkills.push(choice)
    if (second) {
      if (!creature.learnedSkills.includes(second)) creature.learnedSkills.push(second)
      state.starweave!.curriculumAct = state.act
    }
    // 🎓 One School week: the field school teaches the WHOLE team too.
    if (hasWeeklyMod(state, 'oneschool')) {
      for (const mate of state.roster) {
        if (mate.id === creature.id || !canLearnSkill(mate, choice)) continue
        mate.learnedSkills = [...(mate.learnedSkills ?? []), choice]
      }
    }
    useCollection().master('skills', choice)
  }

  /** Applies a rolled outcome; the team then walks home before freeing up. */
  function applyResolution(missionId: string, success: boolean, windfall = false) {
    const state = run.value
    const enc = state?.encounter
    const mission = enc?.missions.find(m => m.id === missionId)
    if (!state || !enc || !mission || mission.status !== 'resolving') return
    // 🎓 v7.20: the First Dispatch reads how its lesson ended (tickFirstDispatch).
    if (state.mode === 'tutorial' && enc.tutorialMissionId === mission.id) enc.tutorialOutcome = success ? 'won' : 'lost'

    const outcome = previewOutcome(mission)
    // v3.37 (per Javi): the walk home is its OWN number now. 🥾
    // Trailblazer zeroes only the trip out, so reading travelTotal (0)
    // was handing it a free ride home too — the bug Javi caught. 💧 Skim,
    // 👑 Decree and 🚀 Fast Deployment set homeTravel 0 deliberately.
    // Missions dispatched before v3.37 fall back to travelTotal.
    const homeTrip = mission.homeTravel ?? mission.travelTotal

    mission.status = success ? 'succeeded' : 'failed'
    mission.removeIn = TUNING.encounters.cardLinger

    // 🎓 Training complete (v0.79): no roll, no goal credit, no damage —
    // the trainee walks home with its chosen skill.
    if (mission.training) {
      mission.status = 'succeeded'
      grantTrainedSkill(state, mission)
      if (hasStarweave(state, 'growth-spark')) for (const creature of outcome.team) grantXp(creature, 5, state)
      enc.returnTrips.push({
        id: makeId('trip'),
        creatureIds: homewardIds(mission), // 🏗️ v6.92: Hold Position stays at the site
        remaining: homeTrip,
        total: homeTrip,
        x: mission.x,
        y: mission.y,
        ctrlX: mission.ctrlX,
        ctrlY: mission.ctrlY,
        poi: mission.poi,
      })
      return
    }

    // (v0.72 dropped the v0.71 mercy rule: chip now reads the FINAL
    // chance, so a shown 100% takes zero damage — no special case.)
    const isSplitBoss = mission.tier === 'boss'
      && (mission.bossSkill === 'fracture' || mission.bossSkill === 'yinyang')
    // bossSplits consumes RNG — call it exactly once and reuse below.
    const splitChildren = success && isSplitBoss ? bossSplits(mission) : []

    if (success) {
      // 🪙 WINDFALL (v2.71): the golden wedge landed. A Windfall is a
      // WIN first — everything below runs unchanged — and the payload
      // is a rider on top. Each owned Windfall relic pays its own; add
      // new payloads here alongside WINDFALL_RELICS in utils/battle.ts.
      if (windfall) {
        const gold = starweaveWindfallGold(state, hasRelic(state, 'relic-prospectors-cut') ? WINDFALL_GOLD : 0)
        state.gold += gold
        actLogFor(state).goldEarned += gold
        if (gold > 0) sfx.coin()
      }
      // A won fight still costs the missing share of its final chance —
      // but never the run: the chip stops at 1 HP (v3.33, per Javi).
      // 🧊 v5.20 (per Javi): wins feed the Warden too — the chip freezes
      // into stats before the clamp (never in puzzles, damage = score).
      const chip = successChipClamp(state.hp, activeNode.value?.type === 'puzzle'
        ? outcome.damageOnSuccess
        : permafrostAbsorb(state, outcome.damageOnSuccess))
      if (chip > 0) {
        const hpBefore = state.hp // 🎓 v7.41 (review): a win's chip is health lost too - the kind defeat books it
        tallyFirstRunDamage(state, 'won', hpBefore, damagePlayer(chip), mission)
      }
      if (hasRelic(state, 'relic-perfect-plan') && !enc.perfectPlanUsed
        && (mission.assignedChance ?? 0) >= 100) {
        enc.perfectPlanUsed = true
      }
      // 🎩 v7.79 (per Javi: "anytime khool fails (even if later composure roll results in a win) i want failure
      // bonuses to apply"): Composure turned a FAILED roll into this win (onResolutionDone stamps composureSaved
      // before calling us). The failure's bonuses land once, here, before the win's own XP; none of its costs do.
      if (mission.composureSaved) onMissionRescued(state, mission, outcome.team, outcome.xpOnFailure)
      // Injury clears first, then XP and level-ups (§21) — but only on
      // Ascension 1 and below; deeper wounds (A2+) shrug off successes.
      // Team Journal rider (v0.77): the redemption mission's success
      // fully cures its team, ANY ascension.
      const journalCure = !!mission.journalBoosted && hasRelic(state, 'relic-team-journal')
      // 🏗️ v6.92 Robodude's Overkill: the team's XP already doubled in the outcome; his point is paid here.
      if (outcome.overkill) for (const engine of outcome.team) if (engine.defId === 'robodude') engine.pendingStatPoints += siegeOverkillPoints(outcome.team)
      for (const creature of outcome.team) {
        if (successClearsInjury(state) || journalCure) healInjuriesFully(creature)
        grantXp(creature, Math.round(outcome.xpOnSuccess * payoutMultiplier(mission) * kilnheartXpMult(creature, mission) * (mission.decreed ? (mission.decreeXpShare ?? 0) : 1)), state)
        if (!enc.succeededIds.includes(creature.id)) enc.succeededIds.push(creature.id)
      }
      // 🩸 v5.42 Battle Scars (elite modifier): winning here still
      // hurts — one random creature on the winning team is injured.
      // Lands AFTER the success-clears pass, so the scar sticks.
      // 🩸 v5.66 Week of Scars: the same law, everywhere, all week
      // (rest-stop cures pay it back — see rewards.ts heal()).
      if ((nodeHasMod('scars') || hasWeeklyMod(state, 'scars')) && outcome.team.length > 0 && !mission.training) {
        const scarred = outcome.team[Math.floor(Math.random() * outcome.team.length)]!
        injureCreature(scarred, state)
      }
      // 👑 v5.12: a beaten champion (or boss wearing one) earns its
      // codex entry HERE — the only way in since spawn-logging exempts
      // champions.
      const championSlug = mission.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      // 🏆 v6.03: beating any painted foe MASTERS its codex entry.
      if (enemyArt(championSlug)) {
        useCollection().master('enemies', championSlug)
      }
      // 🚩 v5.43 Warlord's Banner: the champion falls — the banner
      // drops. Waiting missions shed the tax they spawned with; new
      // spawns arrive untaxed from here on.
      if (nodeHasMod('banner') && CHAMPION_SLUGS.has(championSlug)
        && !enc.bannerLifted) {
        enc.bannerLifted = true
        for (const m of enc.missions) {
          if (m.status !== 'waiting' || !m.bannerTax) continue
          for (const el of m.bannerTax) {
            m.required[el] = Math.max(0, m.required[el] - 1)
          }
          m.bannerTax = undefined
        }
      }
      // 🎯 Skills spent, growth passives banked, 💰 Prospector paid
      // (v2.86/v2.87).
      const prospected = onMissionSucceeded(outcome.team, mission, state.roster, state)
      // 🎩 v7.79: a Composure rescue arms Regroup for the NEXT win (here, after this win's own success hooks).
      if (mission.composureSaved) starweaveArmRegroup(state)
      if (prospected > 0) {
        state.gold += prospected
        actLogFor(state).goldEarned += prospected
      }
      // 🎲 v7.79: a won Wager pays its flat purse on ANY mission (the gold contract below keeps its own price).
      if (outcome.wagerGold > 0) {
        state.gold += outcome.wagerGold
        actLogFor(state).goldEarned += outcome.wagerGold
        if (!mission.goldReward) sfx.coin()
      }
      // Verdant Mending (grass passive): success heals the ranger.
      const mending = passiveSuccessHealing(outcome.team)
      if (mending.heal > 0) state.hp = Math.min(state.maxHp, state.hp + mending.heal)
      for (const id of mending.bonusXpIds) {
        const creature = state.roster.find(c => c.id === id)
        if (creature) grantXp(creature, 1, state)
      }
      // Trained skills (v0.79): 📈 Overclock ramps its top element,
      // 🎓 Mentor tutors its teammates, 👑 Golden Touch pays out.
      for (const creature of outcome.team) {
        if (hasTrainedSkill(creature, 'overclock')) {
          const top = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
          creature.stats[top]++
        }
      }
      if (teamHasTrained(outcome.team, 'mentor')) {
        for (const creature of outcome.team) {
          if (!hasTrainedSkill(creature, 'mentor')) grantXp(creature, 3, state)
        }
      }
      if (teamHasTrained(outcome.team, 'golden-touch')) {
        const bonus = 5 * (hasRelic(state, 'relic-midas-charm') ? 2 : 1)
        state.gold += bonus
        actLogFor(state).goldEarned += bonus
      }
      // 💨/⚓ stacks build on success; armed fail-skills fizzle (the
      // "next mission" they were armed for just resolved).
      for (const creature of outcome.team) {
        // 🧢 v5.02 (per Javi): capped at 5 again (v2.26 lifted it). 💨⚓ v7.63
        // (per Javi): training BOTH no longer lifts the cap by itself - each
        // engine must REACH 5 before either climbs past it (engineCapLifted,
        // read once so a pair at 5 moves on to 6 together). Existing stacks
        // over the cap are never crushed — they just stop climbing.
        const lifted = engineCapLifted(creature)
        if (hasTrainedSkill(creature, 'momentum')) {
          const stacks = creature.momentumStacks ?? 0
          if (lifted || stacks < MOMENTUM_STACK_CAP) creature.momentumStacks = stacks + 1
        }
        if (hasTrainedSkill(creature, 'impulse')) {
          const stacks = creature.impulseStacks ?? 0
          if (lifted || stacks < MOMENTUM_STACK_CAP) creature.impulseStacks = stacks + 1
        }
        creature.armedSkills = consumeArmedSkills(creature)
      }
      // (📣 Rallying Cry moved to DISPATCH in v0.92 — see startFight.)
      // Steady Clock: momentum — every waiting mission gets breathing
      // room. v4.36 (per Javi): NOT bosses — their fuses are the fight's
      // pressure, and farming minion wins was winding the boss's own
      // clock back (split pieces are boss-tier too, so reforms hold).
      const clockBonus = steadyClockBonus(state)
      if (clockBonus > 0) {
        for (const waiting of enc.missions) {
          if (waiting.status === 'waiting' && waiting.tier !== 'boss') {
            waiting.expireTimer += clockBonus
            waiting.expireTotal = Math.max(waiting.expireTotal, waiting.expireTimer)
          }
        }
      }
      actLogFor(state).successes++
      // 📊 v5.64 lifetime ledger (puzzle trials stay out of it).
      if (typeof state.puzzleLevel !== 'number' && state.mode !== 'tutorial') useLifetimeStats().recordMission('succeeded')
      // Gold missions pay out and stop here: no node-goal progress —
      // UNLESS a 🏅 Golden Standard bearer is aboard (v0.81), in which
      // case the win falls through to the normal goal credit below.
      // Midas Charm (v0.76): gold missions pay double.
      if (mission.goldReward) {
        const payout = mission.goldReward
          * (hasRelic(state, 'relic-midas-charm') ? 2 : 1)
          * payoutMultiplier(mission)
          * prospectorGoldMissionMult(outcome.team)
        state.gold += payout
        actLogFor(state).goldEarned += payout
        sfx.coin()
        if (!teamHasTrained(outcome.team, 'golden-standard')) {
          enc.returnTrips.push({
            id: makeId('trip'),
            creatureIds: homewardIds(mission), // 🏗️ v6.92: Hold Position stays at the site
            remaining: homeTrip,
            total: homeTrip,
            x: mission.x,
            y: mission.y,
            ctrlX: mission.ctrlX,
            ctrlY: mission.ctrlY,
            poi: mission.poi,
          })
          return
        }
      }
      // Boss minions never advance the goal: crystals drain the Golem,
      // Dr. Vex just stays squashed for a while.
      if (mission.minion) {
        if (mission.minion === 'crystal') applyCrystalReduction(enc.missions)
        enc.returnTrips.push({
          id: makeId('trip'),
          creatureIds: homewardIds(mission), // 🏗️ v6.92: Hold Position stays at the site
          remaining: homeTrip,
          total: homeTrip,
          x: mission.x,
          y: mission.y,
          ctrlX: mission.ctrlX,
          ctrlY: mission.ctrlY,
          poi: mission.poi,
        })
        return
      }
      // Splitting bosses (Myriad / Yin & Yang): a beaten piece splits
      // instead of falling — and a terminal piece only wins the node
      // once the field is clear of its siblings.
      if (isSplitBoss) {
        const children = splitChildren
        if (children.length > 0) {
          children.forEach((child, index) => {
            // Splits land SYMMETRICALLY: left and right of mid-field —
            // 🏟️ v6.28: a step further out (18/82, was 25/75) so their
            // rings clear the team standing on the inner rim. In the
            // WIDE arena, above and below instead — the left rim is the
            // team's and the right leads to the bridge — with a step
            // right if the spot is taken.
            const wide = forestById(enc.forestId)?.arena?.teamSide === 'left'
            const baseY = Math.min(320, Math.max(90, mission.y))
            const wantX = wide ? 50 : index === 0 ? 18 : 82
            const wantY = wide ? (index === 0 ? baseY - 69 : baseY + 69) : baseY
            const occupied = enc.missions.some(m =>
              m.id !== mission.id
              && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status)
              && Math.abs(m.x - wantX) < 18 && Math.abs(m.y - wantY) < 70,
            )
            const position = wide
              ? { x: occupied ? 68 : wantX, y: wantY }
              : { x: wantX, y: occupied ? (baseY > 205 ? baseY - 100 : baseY + 100) : baseY }
            const fight = Math.max(MIN_FIGHT_TIMER, Math.round(mission.fightTotal * 0.8))
            // Single-element fragments read as "Cores" — the last phase.
            // v4.33 (Javi's emoji sighting): the element is SPELLED, not
            // an emoji baked into the name — "Fire Core", everywhere the
            // name travels.
            const childElements = ELEMENTS.filter(e => child.required[e] > 0)
            const label = child.label === 'Core'
              ? `${ELEMENT_INFO[childElements[0]!].label} Core`
              : child.label
            enc.missions.push({
              id: makeId('m'),
              name: `${mission.name.split(' — ')[0]} — ${label}`,
              tier: 'boss',
              bossSkill: mission.bossSkill,
              fractureDepth: child.depth,
              x: position.x,
              y: position.y,
              ctrlX: position.x,
              ctrlY: (position.y + fieldHome().y) / 2,
              distance: TUNING.travel.minDistance,
              travelRemaining: 0,
              travelTotal: 0,
              required: child.required,
              // Splits expire (v0.51): forces must divide.
              expireTimer: FRACTURE_SPLIT_EXPIRE_SECONDS,
              expireTotal: FRACTURE_SPLIT_EXPIRE_SECONDS,
              fightTimer: fight,
              fightTotal: fight,
              damage: child.damage,
              xp: child.xp,
              slots: child.slots,
              assignedCreatureIds: [],
              assignedChance: null,
              status: 'waiting',
              removeIn: null,
            })
          })
          // The fight goes on — free the team via the return trip below.
          enc.returnTrips.push({
            id: makeId('trip'),
            creatureIds: homewardIds(mission), // 🏗️ v6.92: Hold Position stays at the site
            remaining: homeTrip,
            total: homeTrip,
            x: mission.x,
            y: mission.y,
            ctrlX: mission.ctrlX,
            ctrlY: mission.ctrlY,
            poi: mission.poi,
          })
          return
        }
        const siblingsRemain = enc.missions.some(m =>
          m.id !== mission.id && m.tier === 'boss'
          && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
        )
        if (siblingsRemain) {
          enc.returnTrips.push({
            id: makeId('trip'),
            creatureIds: homewardIds(mission), // 🏗️ v6.92: Hold Position stays at the site
            remaining: homeTrip,
            total: homeTrip,
            x: mission.x,
            y: mission.y,
            ctrlX: mission.ctrlX,
            ctrlY: mission.ctrlY,
            poi: mission.poi,
          })
          return
        }
      }
      // 🌀 v6.91: a swallowed mission is eaten, not completed - no goal credit.
      // 🎓 v7.20: the practice goal counts the two taught wins - a win on the
      // lesson you were told to let expire is welcome but does not count,
      // or it ended the practice before Rillhopper's lessons.
      const practiceAside = state.mode === 'tutorial' && mission.tutorialLesson === 'expire'
      if (!mission.eaten && !practiceAside) enc.successes += 1 + zenithBonusCredit(outcome.team) + (mission.decreedDouble ? 1 : 0)
      // 🚩 Standard Bearer: a SOLO success plants the flag twice.
      if (!mission.eaten && !practiceAside && outcome.team.length === 1 && hasTrainedSkill(outcome.team[0]!, 'standard-bearer')
        && !mission.puzzle) {
        enc.successes += 1 + zenithBonusCredit(outcome.team)
      }
      // Puzzle trials: the node ends when EVERY trial is resolved (the
      // all-success case falls through to finishPuzzleNode too, so the
      // score-based rewards apply).
      if (mission.puzzle) {
        const anyLive = enc.missions.some(m =>
          m.id !== mission.id && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
        )
        if (!anyLive) {
          finishPuzzleNode(state, enc, mission)
          return
        }
        enc.returnTrips.push({
          id: makeId('trip'),
          creatureIds: homewardIds(mission), // 🏗️ v6.92: Hold Position stays at the site
          remaining: homeTrip,
          total: homeTrip,
          x: mission.x,
          y: mission.y,
          ctrlX: mission.ctrlX,
          ctrlY: mission.ctrlY,
          poi: mission.poi,
        })
        return
      }
      if (enc.successes >= enc.goal) {
        // Node goal reached: the encounter ends immediately, everyone is
        // released, and remaining missions disappear safely (§11).
        // 🎓 An in-flight training still graduates — surviving the node
        // is the only requirement.
        for (const inFlight of enc.missions) {
          if (inFlight.training && ['traveling', 'assigned', 'resolving'].includes(inFlight.status)) {
            grantTrainedSkill(state, inFlight)
          }
        }
        // 🔁 Rootstock / Avarice / Understudy (v2.85).
        onNodeComplete(state)
        enc.status = 'complete'
        for (const creature of state.roster) {
          // Dispatched surges are spent even on the instant trip home.
          if (creature.assignedMissionId !== null && creature.surgePrimed) {
            state.surgesFired = (state.surgesFired ?? 0) + 1 // 🧘 v6.54
            creature.surgePrimed = false
            // 📣 A rallied teammate's cooldown is waived (v0.92).
            // ('echo-training' was the legacy id here until v2.16 — it
            // was migrated away on load, so the waiver never fired.)
            creature.surgeCooldown = hasTrainedSkill(creature, 'rallying-cry') || creature.surgeRallied
              ? 0
              : TUNING.skills.cooldownSeconds
          }
          creature.surgeRallied = false
          creature.available = true
          creature.assignedMissionId = null
        }
        enc.missions = enc.missions.filter(m => m.id === mission.id)
        enc.returnTrips = []
        const node = activeNode.value
        // 🎓 v7.39 (per Javi): funnel - a campaign location cleared, and the
        // act-1 boss (the funnel's last step).
        if (funnelCampaign(state)) {
          track('location1_cleared', { once: true })
          if (node?.type === 'boss' && state.act === 1) track('act1_cleared', { once: true })
        }
        // A10's act-3 boss is NOT final (v0.84) — rewards flow, then the
        // 🤫 secret boss reveals on the map.
        const finalBoss = isFinalBossNode(state, node)
        // 🎓 v7.04: the First Dispatch ends on its own card, never the reward screen.
        if (node && !finalBoss && state.mode !== 'tutorial') {
          state.pendingRewardPools = poolsForNode(node.type)
          const pool = state.pendingRewardPools.shift()
          // 🦴 v5.57: daily victory rewards are skeleton (same key
          // shape as advanceRewardStage, which deals later stages).
          state.pendingRewards = pool
            ? withRunStream(state, `offers/${node.id}/${pool}`, () =>
                generateOffers(pool, state, rarityFloor(node.type, pool)))
            : null
        }
        return
      }
    } else {
      actLogFor(state).failures++
      // 📊 v5.64 lifetime ledger (puzzle trials stay out of it).
      if (typeof state.puzzleLevel !== 'number' && state.mode !== 'tutorial') useLifetimeStats().recordMission('failed')
      if (outcome.usesSafetyNet) enc.safetyNetUsed = true
      // 🧊 v5.15: the Warden's pool eats failure chip before the hearts
      // — never in puzzle sessions (damage there is the score). v5.26:
      // discards absorb too (they are expiries; the v5.21 per-node pool
      // already caps any deliberate farming).
      const dawnUsedBefore = state.starweave?.secondDawnUsed
      const hpBefore = state.hp // 🎓 v7.41: the kind defeat's tally books what left the hearts
      const wound = damagePlayer(activeNode.value?.type === 'puzzle'
        ? outcome.damageOnFailure
        : permafrostAbsorb(state, outcome.damageOnFailure))
      tallyFirstRunDamage(state, 'failed', hpBefore, wound, mission)
      const secondDawnSaved = !dawnUsedBefore && !!state.starweave?.secondDawnUsed
      if (outcome.usesMedicBand) enc.medicBandUsed = true
      // 🔁 Every failure BONUS - Martyr, Ashbed, Soulbind, Thornmail's XP (v2.85/v2.87), Team Journal, Field Medic,
      // Tough Learner, Pain Into Power, Grudge - lives in utils/battle failureBonuses (v7.79: a Composure rescue pays
      // them too), run here through onMissionFailed with the failure's costs.
      // 🛡️ Read the Bulwark's XP share BEFORE the resolve pass consumes
      // the primed flag (v3.00).
      const bulwarkShare = bulwarkXpShare(outcome.team)
      const consolation = onMissionFailed(outcome.team, mission, state)
      // 🤕 v5.22 (per Javi): puzzle nodes never injure — the trial's
      // damage score is the whole price of a bad split.
      const puzzleNode = activeNode.value?.type === 'puzzle'
      for (const creature of outcome.team) {
        if (!puzzleNode && !secondDawnSaved && !outcome.injuriesPrevented && !permafrostPrevents(outcome.team, state) && !moonlitPrevents(creature, outcome.team, state)) injureCreature(creature, state)
        grantXp(creature, Math.round(outcome.xpOnFailure * bulwarkShare) + consolation, state)
      }
      // 💨/⚓ stacks collapse on the creature's own failure (a cost: a Composure rescue keeps them).
      for (const creature of outcome.team) {
        creature.momentumStacks = 0
        creature.impulseStacks = 0
        creature.armedSkills = consumeArmedSkills(creature)
      }
      // Puzzle trials: a failure still resolves the trial — check for
      // the end of the puzzle.
      if (mission.puzzle) {
        const anyLive = enc.missions.some(m =>
          m.id !== mission.id && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
        )
        if (!anyLive) {
          finishPuzzleNode(state, enc, mission)
          return
        }
      }
      // 🦈 v5.42 Voracious (elite modifier): a FAILED mission also
      // rises once, hungrier — the boss reforms below handle their own.
      if (nodeHasMod('voracious') && voraciousEligible(mission)) {
        enc.missions.push(voraciousRise(mission))
      }
      // A failed boss split reforms with a fresh timer — bosses never
      // leave. (The root respawns through the spawn loop instead.)
      if (isBossSplit(mission)) {
        enc.missions.push({
          ...mission,
          id: makeId('m'),
          required: { ...mission.required },
          status: 'waiting',
          assignedCreatureIds: [],
          assignedChance: null,
          expireTimer: FRACTURE_SPLIT_EXPIRE_SECONDS,
          expireTotal: FRACTURE_SPLIT_EXPIRE_SECONDS,
          fightTimer: mission.fightTotal,
          travelRemaining: 0,
          removeIn: null,
        })
      }
      // The Sandman (v0.70): a lost fight doesn't send him away — no
      // respawn wait. He stays put and lulls DIFFERENT allies to sleep
      // (v5.27: as many as the ascension demands, fresh sleepers first).
      if (mission.bossSkill === 'knockout' && mission.tier === 'boss') {
        const prev = knockedOutIds(mission)
        const wanted = Math.min(bossAfflictionCount(state.act, state.roster.length), Math.max(1, state.roster.length - 1))
        const fresh = pickRandomIds(state.roster.filter(c => !prev.includes(c.id)).map(c => c.id), wanted)
        const victims = fresh.length < wanted
          ? [...fresh, ...pickRandomIds(prev.filter(id => !fresh.includes(id)), wanted - fresh.length)]
          : fresh
        enc.missions.push({
          ...mission,
          id: makeId('m'),
          required: { ...mission.required },
          status: 'waiting',
          assignedCreatureIds: [],
          assignedChance: null,
          fightTimer: mission.fightTotal,
          travelRemaining: 0,
          removeIn: null,
          knockedOutCreatureId: null,
          knockedOutCreatureIds: victims,
        })
      }
      // Basalt (v0.62): a LOST fight doesn't reset it — it keeps its
      // crystal-drained stats and just winds a fresh fuse. The crystal
      // ring keeps refilling, but more slowly from now on. (Letting the
      // FUSE lapse still reforms it at full strength via the spawn loop.)
      if (mission.bossSkill === 'golem' && mission.tier === 'boss') {
        enc.golemSlowed = true
        enc.missions.push({
          ...mission,
          id: makeId('m'),
          required: { ...mission.required },
          status: 'waiting',
          assignedCreatureIds: [],
          assignedChance: null,
          expireTimer: GOLEM_EXPIRE_SECONDS,
          expireTotal: GOLEM_EXPIRE_SECONDS,
          fightTimer: mission.fightTotal,
          travelRemaining: 0,
          removeIn: null,
        })
      }
    }

    // The trip home takes as long as the trip out, along the same road.
    enc.returnTrips.push({
      starweaveFailed: !success,
      id: makeId('trip'),
      // 🏗️ v7.14 (code review): Hold Position after ORDINARY missions too -
      // this fall-through branch (every plain success and every failure)
      // was the only one still walking him home.
      creatureIds: homewardIds(mission),
      remaining: homeTrip,
      total: homeTrip,
      x: mission.x,
      y: mission.y,
      ctrlX: mission.ctrlX,
      ctrlY: mission.ctrlY,
      poi: mission.poi,
    })
  }

  function finishEncounter() {
    selectedMissionId.value = null
    completeActiveNode()
  }

  /** 🔄 v5.36 (per Javi): when the device rotates mid-node, trade the
   * painting for its orientation twin (see pairedForestId) and re-snap
   * every marker — missions AND homebound walkers — onto the twin's
   * pois. Travel distances stay pinned (fairness: the walk already
   * priced in). Boss thrones and non-family canvases never swap.
   *
   * v5.81 hardening (Javi's rotation report): markers now ALWAYS
   * re-snap to the current forest's poi table — even when no swap
   * happens — and stale poi indices from runs saved before a map-data
   * change (v5.79 cut sites, so an old index can point past the array)
   * wrap into range instead of stranding the marker at dead
   * coordinates. */
  function syncForestOrientation() {
    const enc = run.value?.encounter
    if (!enc?.forestId) return
    const next = pairedForestId(enc.forestId, isLandscapeViewport())
    if (next) enc.forestId = next
    const forest = forestById(enc.forestId)
    if (!forest || forest.pois.length === 0) return
    const snap = (m: { poi?: number; x: number; y: number }) => {
      if (m.poi === undefined) return
      if (!forest.pois[m.poi]) m.poi = m.poi % forest.pois.length
      const poi = forest.pois[m.poi]!
      m.x = poi.x
      m.y = (poi.y / 100) * FIELD_HEIGHT
    }
    for (const mission of enc.missions) snap(mission)
    for (const trip of enc.returnTrips ?? []) snap(trip)
  }

  return {
    encounter,
    activeNode,
    selectedMission,
    resolvingMission,
    ensureEncounter,
    syncForestOrientation,
    tick,
    openMission,
    closeMission,
    discardMission,
    startFight,
    planPuzzleTrial,
    beginPuzzleTrials,
    beginSplitStrike,
    previewOutcome,
    applyResolution,
    finishEncounter,
  }
}
